// =====================================================================
// Resgate Comercial (Dr. Rostand) — v1 REDESIGN
// Identidade visual GENIUS: Material-UI v4 + tema dinâmico (primary do
// settings), Inter, light/dark aware, refinado pra contexto médico/cirúrgico.
// Substitui o painel HTML/Firebase original. Dados via API isolada por
// companyId (JWT). Sem IA (v2).
// =====================================================================
import React, { useEffect, useState, useCallback } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Tooltip,
  Fade,
  RadioGroup,
  Radio,
  FormControlLabel,
  Tabs,
  Tab,
  CircularProgress,
  Checkbox,
  LinearProgress,
  Popover
} from "@material-ui/core";
import { Skeleton, Autocomplete } from "@material-ui/lab";
import { toast } from "react-toastify";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  Add,
  Close,
  Phone,
  WhatsApp,
  PriorityHighRounded,
  Today,
  DateRange,
  CheckCircle,
  Cancel,
  AttachMoney,
  TrendingUp,
  EventAvailable,
  Healing,
  RecordVoiceOver,
  Notes,
  Save,
  DeleteOutline,
  NotificationsActive,
  MoreHoriz,
  LocalHospital,
  MoneyOff,
  FlightTakeoff,
  PhoneDisabled,
  HelpOutline,
  EditOutlined,
  SentimentDissatisfied,
  ArrowForwardIos,
  Schedule,
  Warning,
  AlarmOn,
  Event as EventIcon,
  AssignmentTurnedIn,
  CheckBoxOutlineBlank,
  CheckBox as CheckBoxIcon,
  LocalOffer,
  Tune,
  Palette,
  People,
  Receipt,
  AccountBalance,
  Assessment
} from "@material-ui/icons";

import {
  listRescueLeads,
  getRescueStats,
  createRescueLead,
  updateRescueLead,
  deleteRescueLead,
  listRescueLeadActivities,
  listProcedureOptions
} from "../../services/rescueLeadService";
import {
  listRescueTags,
  createRescueTag,
  updateRescueTag,
  deleteRescueTag,
  setLeadRescueTags
} from "../../services/rescueTagService";
import useIsMobile from "../../utils/useIsMobile";
import api from "../../services/api";
import { useHistory } from "react-router-dom";
import NewTicketModal from "../../components/NewTicketModal";

// ---------- META ----------

const PRIORITIES = {
  urgente: { label: "Ligar Agora", tint: "#e53935", icon: PriorityHighRounded },
  hoje: { label: "Ligar Hoje", tint: "#ff9800", icon: Today },
  semana: { label: "Esta Semana", tint: "#03a9f4", icon: DateRange },
  mensagem: { label: "Mensagem", tint: "#607d8b", icon: NotificationsActive }
};

const STAGES = [
  { key: "ligar", label: "Aguardando Ligação", Icon: Phone, tint: "#607d8b" },
  { key: "contato", label: "Contato Feito", Icon: WhatsApp, tint: "#03a9f4" },
  { key: "agendado", label: "Agendado", Icon: EventAvailable, tint: "#ff9800" },
  { key: "fechado", label: "Fechado", Icon: CheckCircle, tint: "#06c270" },
  { key: "perdido", label: "Perdido", Icon: Cancel, tint: "#e53935" }
];

// Razões de perda padrão (Fase 1 v1.5). Usuária pode digitar texto livre em "Outro".
const LOST_REASONS = [
  { key: "valor",        label: "Valor (achou caro)",         Icon: MoneyOff },
  { key: "cidade",       label: "Cidade / Logística",         Icon: FlightTakeoff },
  { key: "sem_retorno",  label: "Sem retorno (sumiu)",        Icon: PhoneDisabled },
  { key: "mudou_ideia",  label: "Mudou de ideia / Não opera", Icon: SentimentDissatisfied },
  { key: "outro",        label: "Outro motivo (texto livre)", Icon: EditOutlined }
];

// Templates de mensagem WhatsApp por stage (Fase 2 v1.5).
// Placeholders: {nome} = primeiro nome, {data} = consultDate.
// Atalho na ligacao: botao "Abrir WhatsApp" abre wa.me/55<phone>?text=<msg>.
const WA_TEMPLATES = {
  ligar:    "Oi {nome}, tudo bem? Estou entrando em contato sobre a sua consulta — quando posso te ligar?",
  contato:  "Oi {nome}, conforme conversamos, segue o material atualizado. Qualquer dúvida me chama!",
  agendado: "Oi {nome}, lembrete da cirurgia em {data}! Já confirmou os exames pré-operatórios?",
  fechado:  "Oi {nome}, parabéns pela decisão! Está tudo organizado. Qualquer coisa estamos aqui 💛",
  perdido:  ""
};

// Limpa telefone -> só dígitos
const cleanPhone = (raw) => String(raw || "").replace(/\D/g, "");

// Abre WhatsApp com template pré-preenchido baseado no stage atual do lead.
// 55 e prefixado se nao vier. Retorna false se nao tem telefone.
const openWhatsApp = (lead) => {
  const phone = cleanPhone(lead && lead.phone);
  if (!phone) {
    toast.warn("Lead sem telefone cadastrado — edite o lead pra adicionar");
    return false;
  }
  const tpl = WA_TEMPLATES[lead.stage] || "";
  const firstName = (lead.name || "").trim().split(/\s+/)[0] || "";
  const msg = tpl
    .replace(/\{nome\}/g, firstName)
    .replace(/\{data\}/g, lead.consultDate || "—");
  const fullPhone = phone.startsWith("55") ? phone : "55" + phone;
  const url = "https://wa.me/" + fullPhone + (msg ? "?text=" + encodeURIComponent(msg) : "");
  window.open(url, "_blank", "noopener,noreferrer");
  return true;
};

// Computa badge do proximo contato: ATRASADO Xd / HOJE / em Xd / null.
// Fase 2 v1.5. Aceita ISO string ou Date. Null retorna null (sem badge).
const computeNextBadge = (nextAt) => {
  if (!nextAt) return null;
  const d = new Date(nextAt);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.floor((target.getTime() - today.getTime()) / 86400000);
  if (diffDays < 0)  return { kind: "overdue", label: `ATRASADO ${-diffDays}d`, tint: "#e53935", priority: 0, Icon: Warning };
  if (diffDays === 0) return { kind: "today",   label: "HOJE",                 tint: "#ff9800", priority: 1, Icon: AlarmOn };
  if (diffDays <= 7) return { kind: "soon",    label: `em ${diffDays}d`,      tint: "#03a9f4", priority: 2, Icon: Schedule };
  return null; // > 7 dias: sem badge pra nao poluir
};

// Avanca o lead pro proximo stage no funil de venda (nao avanca pra perdido).
const STAGE_FLOW = ["ligar", "contato", "agendado", "fechado"];

// Helper input datetime-local: HTML5 native. Aceita ISO string -> string local.
const toDatetimeLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const brl = (v) =>
  Number(v || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0
  });

const initials = (name) => {
  const s = (name || "").trim();
  if (!s) return "—";
  const parts = s.split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]).join("").toUpperCase();
};

// ---------- STYLES (theme-aware Genius) ----------

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
    minHeight: "calc(100vh - 64px)",
    ...theme.scrollbarStyles
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
    gap: theme.spacing(2)
  },
  titleBlock: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2)
  },
  titleIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
    color: theme.palette.primary.contrastText,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: `0 4px 12px ${theme.palette.primary.main}40`
  },
  pageTitle: {
    fontWeight: 700,
    fontSize: "1.5rem",
    letterSpacing: "-0.025em",
    color: theme.palette.text.primary,
    lineHeight: 1.2
  },
  pageSubtitle: {
    fontSize: "0.8125rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
    fontWeight: 500
  },
  primaryAction: {
    padding: theme.spacing(1, 2.5),
    borderRadius: 10,
    fontWeight: 600,
    fontSize: "0.875rem",
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    boxShadow: `0 2px 8px ${theme.palette.primary.main}40`,
    "&:hover": {
      background: theme.palette.primary.dark,
      boxShadow: `0 4px 12px ${theme.palette.primary.main}60`,
      transform: "translateY(-1px)"
    }
  },
  statsGrid: { marginBottom: theme.spacing(2.25) },
  // F1.6 v1.5 (S29): header de seção entre os blocos de stats
  // (Performance do Funil / Operação do Dia). Etiqueta uppercase pequena +
  // linha divisória sutil pra dar hierarquia visual sem poluir.
  statsSectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.25),
    paddingLeft: 2
  },
  statsSectionTitle: {
    fontSize: "0.6875rem",
    fontWeight: 700,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.08em"
  },
  statsSectionDivider: {
    flex: 1,
    height: 1,
    background: theme.palette.divider,
    marginLeft: theme.spacing(1)
  },
  statCard: {
    padding: theme.spacing(2),
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 1px 3px rgba(0,0,0,0.3)"
        : "0 1px 3px rgba(0,0,0,0.05)",
    transition: "all 0.25s ease",
    height: "100%",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow:
        theme.palette.type === "dark"
          ? "0 6px 16px rgba(0,0,0,0.4)"
          : "0 6px 16px rgba(0,0,0,0.1)"
    }
  },
  // F1.6 v1.5 (S29): destaque visual nos 2 cards mais importantes —
  // Conversão e Receita Ganha. Borda colorida + gradiente sutil + shadow
  // mais forte. Tint do próprio card é injetada via style inline (---tint).
  statCardHighlight: {
    "&[data-highlight=true]": {
      borderWidth: 1.5,
      "&:hover": {
        transform: "translateY(-2px)"
      }
    }
  },
  statIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  statValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    lineHeight: 1.1,
    letterSpacing: "-0.025em",
    color: theme.palette.text.primary
  },
  statValueHighlight: {
    fontSize: "1.6rem"
  },
  statLabel: {
    fontSize: "0.6875rem",
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginTop: 2,
    fontWeight: 600
  },
  // F1.6 v1.5 (S29): sub-label descritiva embaixo do label principal
  // (ex: "X de Y decididos" abaixo de "Cirurgias Fechadas").
  statSubLabel: {
    fontSize: "0.625rem",
    color: theme.palette.text.secondary,
    marginTop: 1,
    fontWeight: 500,
    opacity: 0.82,
    lineHeight: 1.3
  },
  board: {
    display: "flex",
    gap: theme.spacing(2),
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    paddingBottom: theme.spacing(2),
    ...theme.scrollbarStyles
  },
  column: {
    minWidth: 300,
    width: 300,
    flexShrink: 0,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.02)"
        : "rgba(0,0,0,0.02)",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(1.5),
    display: "flex",
    flexDirection: "column",
    maxHeight: "calc(100vh - 280px)"
  },
  columnHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1.5),
    paddingLeft: theme.spacing(0.5)
  },
  columnTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    fontSize: "0.75rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    textTransform: "uppercase",
    letterSpacing: "0.06em"
  },
  columnCount: {
    fontSize: "0.6875rem",
    fontWeight: 700,
    color: theme.palette.text.secondary,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.06)"
        : "rgba(0,0,0,0.06)",
    padding: theme.spacing(0.25, 1),
    borderRadius: 12,
    minWidth: 22,
    textAlign: "center"
  },
  columnBody: {
    overflowY: "auto",
    flex: 1,
    paddingRight: 2,
    ...theme.scrollbarStylesSoft
  },
  leadCard: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 10,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow:
        theme.palette.type === "dark"
          ? "0 8px 20px rgba(0,0,0,0.45)"
          : "0 8px 20px rgba(0,0,0,0.08)",
      borderColor: theme.palette.primary.main
    }
  },
  leadTop: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  leadAvatar: {
    width: 36,
    height: 36,
    fontSize: "0.8125rem",
    fontWeight: 700,
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
    color: theme.palette.primary.contrastText,
    flexShrink: 0
  },
  leadIdentity: { flex: 1, minWidth: 0 },
  leadName: {
    fontWeight: 600,
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    lineHeight: 1.3
  },
  leadProcedure: {
    fontSize: "0.6875rem",
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginTop: 1
  },
  leadFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing(0.75)
  },
  leadValue: {
    fontSize: "0.875rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
    whiteSpace: "nowrap"
  },
  priorityChip: {
    height: 22,
    fontSize: "0.6875rem",
    fontWeight: 600,
    borderRadius: 8
  },
  emptyColumn: {
    textAlign: "center",
    color: theme.palette.text.disabled,
    fontSize: "0.75rem",
    padding: theme.spacing(3, 1),
    fontStyle: "italic"
  },
  loadingWrap: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(8)
  },
  // Fase 1 v1.5 — Mini funil + banner perda
  miniFunnel: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.palette.type === "dark"
      ? "0 1px 3px rgba(0,0,0,0.3)"
      : "0 1px 3px rgba(0,0,0,0.05)"
  },
  miniFunnelTitle: {
    fontSize: "0.6875rem",
    fontWeight: 700,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75)
  },
  miniFunnelBar: {
    display: "flex",
    flex: 1,
    height: 28,
    borderRadius: 8,
    overflow: "hidden",
    background: theme.palette.type === "dark"
      ? "rgba(255,255,255,0.04)"
      : "rgba(0,0,0,0.04)"
  },
  miniFunnelSegment: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.6875rem",
    fontWeight: 700,
    color: "#fff",
    padding: theme.spacing(0, 1),
    transition: "flex 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    minWidth: 32,
    cursor: "default"
  },
  miniFunnelEmpty: {
    fontSize: "0.75rem",
    color: theme.palette.text.disabled,
    fontStyle: "italic",
    paddingLeft: theme.spacing(2)
  },
  lostBanner: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2),
    borderRadius: 10,
    background: theme.palette.type === "dark"
      ? "rgba(229,57,53,0.12)"
      : "rgba(229,57,53,0.08)",
    border: `1px solid ${theme.palette.error.main}30`,
    borderLeft: `3px solid ${theme.palette.error.main}`
  },
  // Fase 2 v1.5 — Quick actions hover overlay + nextContact badge
  quickActions: {
    position: "absolute",
    top: 6,
    right: 6,
    display: "flex",
    gap: 2,
    opacity: 0,
    pointerEvents: "none",
    transition: "opacity 0.15s ease",
    background: theme.palette.background.paper,
    borderRadius: 8,
    padding: 2,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 4px 12px rgba(0,0,0,0.45)"
        : "0 4px 12px rgba(0,0,0,0.1)",
    zIndex: 2,
    "& button": {
      width: 28,
      height: 28,
      padding: 0
    }
  },
  leadCardWithActions: {
    position: "relative",
    "&:hover $quickActions": {
      opacity: 1,
      pointerEvents: "auto"
    }
  },
  nextBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    height: 20,
    padding: theme.spacing(0, 0.75),
    borderRadius: 6,
    fontSize: "0.625rem",
    fontWeight: 700,
    letterSpacing: "0.02em",
    whiteSpace: "nowrap",
    border: "1px solid transparent",
    marginRight: theme.spacing(0.5)
  },
  nextBadgeOverdue: {
    background: "rgba(229,57,53,0.15)",
    color: "#e53935",
    borderColor: "rgba(229,57,53,0.35)",
    animation: "$pulseOverdue 2.4s ease-in-out infinite"
  },
  nextBadgeToday: {
    background: "rgba(255,152,0,0.15)",
    color: "#ff9800",
    borderColor: "rgba(255,152,0,0.35)"
  },
  nextBadgeSoon: {
    background: "rgba(3,169,244,0.15)",
    color: "#03a9f4",
    borderColor: "rgba(3,169,244,0.35)"
  },
  "@keyframes pulseOverdue": {
    "0%, 100%": { boxShadow: "0 0 0 0 rgba(229,57,53,0.4)" },
    "50%":      { boxShadow: "0 0 0 5px rgba(229,57,53,0)" }
  },
  waButton: {
    background: "#25D366",
    color: "#fff",
    "&:hover": { background: "#1ebe5a" }
  },
  nextContactInput: {
    width: "100%",
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1, 1.25),
    color: theme.palette.text.primary,
    fontSize: "0.875rem",
    fontFamily: "inherit",
    colorScheme: theme.palette.type === "dark" ? "dark" : "light",
    outline: "none",
    "&:focus": { borderColor: theme.palette.primary.main }
  },
  // Fase 3 v1.5 — Drag-and-drop visual feedback
  columnDragOver: {
    background:
      theme.palette.type === "dark"
        ? `${theme.palette.primary.main}15`
        : `${theme.palette.primary.main}0a`,
    borderColor: theme.palette.primary.main,
    transition: "background 0.2s ease, border-color 0.2s ease"
  },
  // Fase 3 v1.5 — Tabs + Timeline UI
  tabsRoot: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 40
  },
  tab: {
    minHeight: 40,
    fontSize: "0.8125rem",
    fontWeight: 600,
    textTransform: "none",
    letterSpacing: 0.2,
    padding: theme.spacing(0.5, 2)
  },
  timelineWrap: {
    padding: theme.spacing(2, 0),
    maxHeight: 480,
    overflowY: "auto",
    ...theme.scrollbarStylesSoft
  },
  timelineEmpty: {
    textAlign: "center",
    padding: theme.spacing(6, 2),
    color: theme.palette.text.disabled,
    fontSize: "0.875rem"
  },
  timelineItem: {
    display: "flex",
    gap: theme.spacing(1.5),
    paddingBottom: theme.spacing(2),
    position: "relative",
    "&:not(:last-child)::before": {
      content: '""',
      position: "absolute",
      left: 15,
      top: 32,
      bottom: 0,
      width: 2,
      background: theme.palette.divider
    }
  },
  timelineDot: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    zIndex: 1,
    border: `2px solid ${theme.palette.background.paper}`,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 1px 3px rgba(0,0,0,0.3)"
        : "0 1px 3px rgba(0,0,0,0.1)"
  },
  timelineContent: {
    flex: 1,
    minWidth: 0,
    paddingTop: theme.spacing(0.5)
  },
  timelineDesc: {
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
    lineHeight: 1.4
  },
  timelineMeta: {
    fontSize: "0.6875rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75)
  },
  // Fase 4.2 v1.5 — Checklist pre-cirurgia
  checklistHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.25),
    flexWrap: "wrap"
  },
  checklistProgressWrap: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 140
  },
  checklistCount: {
    fontSize: "0.75rem",
    fontWeight: 700,
    letterSpacing: "0.02em",
    fontFeatureSettings: '"tnum"',
    whiteSpace: "nowrap"
  },
  checklistBarOuter: {
    flex: 1,
    minWidth: 80
  },
  checklistBar: {
    height: 6,
    borderRadius: 3,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.08)"
        : "rgba(0,0,0,0.06)"
  },
  checklistBarFill: {
    borderRadius: 3,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
  },
  checklistBarFillDone: {
    borderRadius: 3,
    background: theme.palette.success.main
  },
  checklistItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.75, 1),
    borderRadius: 8,
    cursor: "pointer",
    transition: "background 0.15s ease",
    "&:hover": {
      background:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.04)"
          : "rgba(0,0,0,0.03)"
    },
    "& + &": {
      marginTop: 2
    }
  },
  checklistItemDone: {
    "& $checklistLabel": {
      color: theme.palette.text.secondary
    }
  },
  checklistCheckbox: {
    padding: theme.spacing(0.5)
  },
  checklistLabel: {
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
    lineHeight: 1.4
  },
  checklistLabelDone: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
    textDecoration: "line-through"
  },
  checklistAt: {
    fontSize: "0.6875rem",
    color: theme.palette.success.main,
    marginTop: 2,
    fontWeight: 500
  },
  // Fase 4.3 v1.5 — Tags no card e no detalhe
  cardTagsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
    margin: theme.spacing(0.75, 0, 0.5, 0),
    paddingTop: theme.spacing(0.5),
    borderTop: `1px dashed ${theme.palette.divider}`
  },
  cardTagChip: {
    display: "inline-flex",
    alignItems: "center",
    padding: theme.spacing(0.25, 0.875),
    borderRadius: 10,
    fontSize: "0.625rem",
    fontWeight: 700,
    letterSpacing: "0.02em",
    maxWidth: 120,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    lineHeight: 1.5,
    color: "#fff",
    boxShadow: "0 1px 2px rgba(0,0,0,0.12)"
  },
  cardTagOverflow: {
    display: "inline-flex",
    alignItems: "center",
    padding: theme.spacing(0.25, 0.5),
    borderRadius: 4,
    fontSize: "0.625rem",
    fontWeight: 700,
    color: theme.palette.text.secondary,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.06)"
        : "rgba(0,0,0,0.05)"
  },
  tagsHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  manageTagsBtn: {
    textTransform: "none",
    fontSize: "0.75rem",
    fontWeight: 600,
    color: theme.palette.primary.main,
    padding: theme.spacing(0.25, 1),
    minHeight: 28
  },
  tagsPickerRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6
  },
  tagPickerChip: {
    display: "inline-flex",
    alignItems: "center",
    padding: theme.spacing(0.5, 1),
    borderRadius: 14,
    fontSize: "0.75rem",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.15s ease",
    userSelect: "none",
    "&:hover": { opacity: 1 }
  },
  // Fase 5 v1.6 — Tags polidas
  assignedTagChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: theme.spacing(0.5, 1.25),
    borderRadius: 16,
    fontSize: "0.75rem",
    fontWeight: 600,
    cursor: "pointer",
    color: "#fff",
    boxShadow: "0 1px 3px rgba(0,0,0,0.18), 0 1px 2px rgba(0,0,0,0.12)",
    transition: "transform 0.18s cubic-bezier(0.4,0,0.2,1), box-shadow 0.18s ease",
    userSelect: "none",
    letterSpacing: "0.01em",
    "&:hover": {
      boxShadow: "0 3px 8px rgba(0,0,0,0.22), 0 2px 4px rgba(0,0,0,0.16)",
      transform: "translateY(-1px)"
    },
    "&:active": {
      transform: "translateY(0)"
    }
  },
  availableTagChip: {
    display: "inline-flex",
    alignItems: "center",
    padding: theme.spacing(0.5, 1.25),
    borderRadius: 16,
    fontSize: "0.75rem",
    fontWeight: 600,
    cursor: "pointer",
    transition: "transform 0.18s cubic-bezier(0.4,0,0.2,1), box-shadow 0.18s ease, opacity 0.18s ease",
    userSelect: "none",
    letterSpacing: "0.01em",
    opacity: 0.92,
    "&:hover": {
      transform: "scale(1.05)",
      opacity: 1,
      boxShadow: "0 2px 6px rgba(0,0,0,0.12)"
    }
  },
  addTagButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 2,
    padding: theme.spacing(0.5, 1.25),
    borderRadius: 16,
    fontSize: "0.75rem",
    fontWeight: 600,
    cursor: "pointer",
    background: "transparent",
    color: theme.palette.text.secondary,
    border: `1px dashed ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.25)"
        : "rgba(0,0,0,0.22)"
    }`,
    transition: "all 0.18s cubic-bezier(0.4,0,0.2,1)",
    userSelect: "none",
    "&:hover": {
      background:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.05)"
          : "rgba(0,0,0,0.03)",
      borderColor: theme.palette.primary.main,
      borderStyle: "solid",
      color: theme.palette.primary.main,
      transform: "translateY(-1px)"
    }
  },
  tagCloseIcon: {
    fontSize: 13,
    marginLeft: 2,
    borderRadius: "50%",
    padding: 1,
    transition: "background 0.15s ease",
    "&:hover": {
      background: "rgba(255,255,255,0.25)"
    }
  },
  tagPopoverWrapper: {
    padding: theme.spacing(1.5, 1.75),
    maxWidth: 360,
    minWidth: 240,
    background: theme.palette.background.paper
  },
  tagPopoverTitle: {
    fontSize: "0.6875rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.25),
    display: "flex",
    alignItems: "center",
    gap: 6
  },
  tagPopoverGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6
  },
  tagPopoverEmpty: {
    fontSize: "0.8125rem",
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    padding: theme.spacing(0.5, 0)
  },
  // ManageTags dialog (CRUD admin)
  tagDialogList: {
    marginTop: theme.spacing(1.5),
    display: "flex",
    flexDirection: "column",
    gap: 6,
    maxHeight: 320,
    overflowY: "auto",
    ...theme.scrollbarStylesSoft
  },
  tagDialogRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.02)"
        : theme.palette.background.paper
  },
  tagSwatch: {
    width: 18,
    height: 18,
    borderRadius: 4,
    flexShrink: 0,
    border: "1px solid rgba(0,0,0,0.12)"
  },
  tagDialogName: {
    flex: 1,
    fontSize: "0.875rem",
    fontWeight: 600,
    color: theme.palette.text.primary
  },
  tagFormRow: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    marginTop: theme.spacing(1.5),
    flexWrap: "wrap"
  },
  tagColorPalette: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4
  },
  tagColorDot: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    cursor: "pointer",
    border: "2px solid transparent",
    transition: "transform 0.15s ease"
  },
  tagColorDotSelected: {
    border: `2px solid ${theme.palette.text.primary}`,
    transform: "scale(1.1)"
  },
  // Fase 4.1 v1.5 — Modo Foco da Ligacao
  focusBtn: {
    textTransform: "none",
    fontWeight: 700,
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      filter: "brightness(1.05)"
    }
  },
  focusPaper: {
    background:
      theme.palette.type === "dark"
        ? theme.palette.background.default
        : "#f7f9fc"
  },
  focusHeader: {
    padding: theme.spacing(2, 3),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper
  },
  focusAvatar: {
    width: 64,
    height: 64,
    fontSize: "1.5rem",
    fontWeight: 700,
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
    color: theme.palette.primary.contrastText
  },
  focusIdentity: {
    flex: 1,
    minWidth: 0
  },
  focusName: {
    fontSize: "1.5rem",
    fontWeight: 700,
    letterSpacing: "-0.025em",
    color: theme.palette.text.primary,
    lineHeight: 1.1
  },
  focusSub: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap"
  },
  focusPriorityChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: theme.spacing(0.25, 1),
    borderRadius: 4,
    fontSize: "0.6875rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em"
  },
  focusBody: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(3, 3, 2, 3),
    background: "transparent",
    ...theme.scrollbarStylesSoft
  },
  focusGrid: {
    display: "grid",
    gridTemplateColumns: "300px 1fr 280px",
    gap: theme.spacing(3),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
      gap: theme.spacing(2)
    }
  },
  focusCard: {
    padding: theme.spacing(2),
    borderRadius: 12,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 1px 3px rgba(0,0,0,0.3)"
        : "0 1px 3px rgba(0,0,0,0.04)"
  },
  focusCardTitle: {
    fontSize: "0.6875rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginBottom: theme.spacing(1.5)
  },
  focusPhoneBig: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    letterSpacing: "0.02em",
    fontFeatureSettings: '"tnum"',
    textDecoration: "none",
    "&:hover": { color: theme.palette.primary.main }
  },
  focusInfoRow: {
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
    marginTop: theme.spacing(1)
  },
  focusInfoLabel: {
    fontSize: "0.6875rem",
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontWeight: 600,
    marginBottom: 2,
    display: "block"
  },
  focusWaBtn: {
    marginTop: theme.spacing(2),
    background: "#25D366",
    color: "#fff",
    fontWeight: 700,
    fontSize: "0.9375rem",
    width: "100%",
    padding: theme.spacing(1.25, 2),
    "&:hover": { background: "#1ebe57" }
  },
  focusScriptStep: {
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px dashed ${theme.palette.divider}`,
    "&:last-child": { borderBottom: "none", marginBottom: 0 }
  },
  focusScriptTitle: {
    fontSize: "0.875rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  focusScriptText: {
    fontSize: "1rem",
    lineHeight: 1.6,
    color: theme.palette.text.primary,
    whiteSpace: "pre-wrap"
  },
  focusScriptNote: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    borderRadius: 6,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,235,59,0.08)"
        : "rgba(255,235,59,0.18)",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,235,59,0.25)"
        : "rgba(255,235,59,0.5)"
    }`,
    fontSize: "0.8125rem",
    color: theme.palette.text.primary,
    lineHeight: 1.4
  },
  focusObjectionItem: {
    padding: theme.spacing(1.25),
    borderRadius: 8,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.03)"
        : "rgba(0,0,0,0.025)",
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(1),
    "&:last-child": { marginBottom: 0 }
  },
  focusObjectionLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    fontWeight: 600,
    marginBottom: 4
  },
  focusObjectionResp: {
    fontSize: "0.8125rem",
    color: theme.palette.text.primary,
    lineHeight: 1.4,
    fontStyle: "italic"
  },
  focusResultBar: {
    padding: theme.spacing(2, 3),
    background: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "flex-end",
    flexWrap: "wrap"
  },
  focusResultBtn: {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.875rem",
    padding: theme.spacing(1, 2.5),
    minWidth: 140
  },
  // Fase 4.4 v1.5 — View toggle + Calendar/Agenda view
  viewToggle: {
    display: "inline-flex",
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: 2
  },
  viewToggleBtn: {
    borderRadius: 6,
    padding: theme.spacing(0.5, 1.25),
    color: theme.palette.text.secondary
  },
  viewToggleBtnActive: {
    background: `${theme.palette.primary.main}1f`,
    color: theme.palette.primary.main
  },
  calendarRoot: {
    marginTop: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    padding: theme.spacing(2),
    background: theme.palette.background.paper,
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`
  },
  calendarEmpty: {
    padding: theme.spacing(6, 2),
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontSize: "0.9375rem"
  },
  calendarMonthGroup: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1)
  },
  calendarMonthTitle: {
    fontSize: "0.875rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginBottom: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    borderBottom: `2px solid ${theme.palette.primary.main}20`
  },
  calendarRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    cursor: "pointer",
    transition: "all 0.15s ease",
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.02)"
        : theme.palette.background.default,
    "&:hover": {
      borderColor: theme.palette.primary.main,
      background:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.04)"
          : "rgba(0,0,0,0.02)"
    }
  },
  calendarDateBadge: {
    flexShrink: 0,
    width: 56,
    height: 56,
    borderRadius: 10,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
    color: theme.palette.primary.contrastText
  },
  calendarDateDay: {
    fontSize: "1.5rem",
    fontWeight: 700,
    lineHeight: 1
  },
  calendarDateMonth: {
    fontSize: "0.625rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginTop: 2
  },
  calendarRowMain: {
    flex: 1,
    minWidth: 0
  },
  calendarRowName: {
    fontSize: "1rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1.2
  },
  calendarRowMeta: {
    fontSize: "0.8125rem",
    color: theme.palette.text.secondary,
    marginTop: 4,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap"
  },
  calendarChecklistChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: theme.spacing(0.25, 0.75),
    borderRadius: 4,
    fontSize: "0.6875rem",
    fontWeight: 700,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.06)"
        : "rgba(0,0,0,0.05)",
    color: theme.palette.text.secondary
  },
  // ------- Dialog -------
  dialogPaper: {
    borderRadius: 16,
    [theme.breakpoints.up("sm")]: { minWidth: 600 }
  },
  dialogHeader: {
    padding: theme.spacing(3, 3, 2, 3),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  dialogHeaderIdentity: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flex: 1,
    minWidth: 0
  },
  dialogAvatar: {
    width: 48,
    height: 48,
    fontSize: "1rem",
    fontWeight: 700,
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
    color: theme.palette.primary.contrastText,
    flexShrink: 0
  },
  dialogName: {
    fontWeight: 700,
    letterSpacing: "-0.025em",
    fontSize: "1.25rem",
    lineHeight: 1.2,
    color: theme.palette.text.primary
  },
  dialogSubtitle: {
    fontSize: "0.8125rem",
    color: theme.palette.text.secondary,
    marginTop: 2
  },
  dialogBody: {
    padding: theme.spacing(2.5, 3),
    ...theme.scrollbarStylesSoft
  },
  metaGrid: { marginBottom: theme.spacing(2) },
  metaField: {},
  metaLabel: {
    fontSize: "0.6875rem",
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: 4,
    fontWeight: 600
  },
  metaValue: {
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
    lineHeight: 1.5
  },
  sectionBlock: {
    marginTop: theme.spacing(2.5),
    padding: theme.spacing(2),
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.025)"
        : "rgba(0,0,0,0.02)",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12
  },
  sectionTitle: {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: theme.spacing(1.5),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75)
  },
  budgetRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1.25, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": { borderBottom: "none", paddingBottom: 0 },
    "&:first-child": { paddingTop: 0 }
  },
  budgetCity: { fontWeight: 600, fontSize: "0.875rem", lineHeight: 1.3 },
  budgetMeta: {
    fontSize: "0.6875rem",
    color: theme.palette.text.secondary,
    marginTop: 2
  },
  budgetTotal: {
    fontWeight: 700,
    color: theme.palette.primary.main,
    fontSize: "1rem",
    whiteSpace: "nowrap"
  },
  dateGroup: { marginBottom: theme.spacing(1.5), "&:last-child": { marginBottom: 0 } },
  dateGroupLabel: {
    fontSize: "0.6875rem",
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontWeight: 600,
    marginBottom: theme.spacing(0.75)
  },
  dateChips: { display: "flex", gap: theme.spacing(0.75), flexWrap: "wrap" },
  dateChip: {
    height: 24,
    fontSize: "0.75rem",
    borderRadius: 8,
    background:
      theme.palette.type === "dark"
        ? `${theme.palette.primary.main}22`
        : `${theme.palette.primary.main}15`,
    color: theme.palette.primary.main,
    fontWeight: 600,
    border: `1px solid ${theme.palette.primary.main}30`
  },
  scriptItem: {
    padding: theme.spacing(1.25, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": { borderBottom: "none" }
  },
  scriptStep: {
    fontSize: "0.8125rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.75),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75)
  },
  scriptNumber: {
    width: 22,
    height: 22,
    borderRadius: 6,
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontSize: "0.6875rem",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  scriptNote: {
    fontSize: "0.75rem",
    color: theme.palette.type === "dark" ? "#ffd54f" : "#a07300",
    background:
      theme.palette.type === "dark"
        ? "rgba(255,193,7,0.08)"
        : "rgba(255,193,7,0.1)",
    borderLeft: `3px solid ${theme.palette.warning.main}`,
    padding: theme.spacing(0.75, 1.25),
    borderRadius: 4,
    marginTop: theme.spacing(0.75),
    lineHeight: 1.5
  },
  objectionItem: {
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(3,169,244,0.08)"
        : "rgba(3,169,244,0.06)",
    borderLeft: `3px solid ${theme.palette.info.main}`,
    padding: theme.spacing(1.25),
    borderRadius: 8,
    marginBottom: theme.spacing(1),
    "&:last-child": { marginBottom: 0 }
  },
  objectionLabel: {
    fontSize: "0.6875rem",
    color: theme.palette.text.secondary,
    fontWeight: 600,
    fontStyle: "italic"
  },
  objectionResp: {
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
    lineHeight: 1.5,
    marginTop: 4
  },
  dialogActionsRow: {
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
    justifyContent: "space-between"
  },
  deleteBtn: {
    color: theme.palette.error.main,
    border: `1px solid ${theme.palette.error.main}40`,
    "&:hover": { backgroundColor: `${theme.palette.error.main}10` }
  },
  saveBtn: {
    background: theme.palette.success.main,
    color: "#fff",
    "&:hover": { background: theme.palette.success.dark }
  }
}));

// ---------- COMPONENT ----------

const ResgateComercial = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useIsMobile();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [novoOpen, setNovoOpen] = useState(false);
  const [scriptDraft, setScriptDraft] = useState([]);
  const [notaDraft, setNotaDraft] = useState("");
  // S45 A8: orçamentos editáveis (array de {cidade, cirurgia, hospital, total})
  const [budgetsDraft, setBudgetsDraft] = useState([]);
  // S45 A9: datas disponíveis editáveis (objeto { cidadeKey: [datas] })
  const [datesDraft, setDatesDraft] = useState({});
  const [newDateInputs, setNewDateInputs] = useState({}); // input por cidade
  const [newCityName, setNewCityName] = useState("");
  // S45 A12: totalValue editável (string raw pra liberdade na digitação)
  const [valorDraft, setValorDraft] = useState("");
  // S45 A14: telefone + procedimento editáveis no detalhe
  const [phoneDraft, setPhoneDraft] = useState("");
  const [procedureDraft, setProcedureDraft] = useState("");
  // Lista de procedimentos do tenant (clinic_services). Vazia se admin sem acesso.
  const [availableServices, setAvailableServices] = useState([]);
  // Fase 1 v1.5: dialog do motivo de perda (quando muda stage pra 'perdido')
  const [lostFor, setLostFor] = useState(null);
  // Fase 3 v1.5: tab do modal de detalhe (0=Detalhes, 1=Histórico)
  const [detailTab, setDetailTab] = useState(0);
  // Fase 4.3 v1.5: tags do tenant + dialog de gerenciamento
  const [tags, setTags] = useState([]);
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  // Fase 5 v1.6: popover de atribuir tag (mostra so disponiveis)
  const [tagPopoverAnchor, setTagPopoverAnchor] = useState(null);
  // Fase 4.1 v1.5: Modo Foco da Ligacao (fullscreen)
  const [focusLead, setFocusLead] = useState(null);
  // Fase 4.4 v1.5: view toggle (board | calendar)
  const [viewMode, setViewMode] = useState("board");
  // [S113] equipe pro seletor de "Responsável" (atribuição de lead)
  const [teamUsers, setTeamUsers] = useState([]);
  // [S113] abrir conversa DENTRO do CRM (em vez de wa.me externo)
  const history = useHistory();
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [ticketContact, setTicketContact] = useState(null);

  const reload = useCallback(async () => {
    try {
      const [l, s, t] = await Promise.all([
        listRescueLeads(),
        getRescueStats(),
        listRescueTags().catch(() => [])
      ]);
      setLeads(Array.isArray(l) ? l : []);
      setStats(s || {});
      setTags(Array.isArray(t) ? t : []);
    } catch (e) {
      console.error("[ResgateComercial] load", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  // [S113] carrega a equipe (Users da company) pro seletor de responsável.
  useEffect(() => {
    api
      .get("/users", { params: { pageNumber: 1 } })
      .then(({ data }) =>
        setTeamUsers(Array.isArray(data?.users) ? data.users : [])
      )
      .catch(() => setTeamUsers([]));
  }, []);

  // S45 A14/A15: carrega procedimentos do tenant pra autocomplete via endpoint
  // dedicado /rescue-leads/procedures-options (aberto pra qualquer profile,
  // isolado por JWT.companyId). Fallback gracioso se a tabela não existir.
  useEffect(() => {
    let cancelled = false;
    const loadProcedures = async () => {
      try {
        const names = await listProcedureOptions();
        if (cancelled) return;
        if (Array.isArray(names) && names.length > 0) {
          // Dedup case-insensitive + preserva ordem do backend
          const seen = new Set();
          const uniq = [];
          names.forEach((n) => {
            const k = String(n || "").trim().toLowerCase();
            if (k && !seen.has(k)) {
              seen.add(k);
              uniq.push(n);
            }
          });
          setAvailableServices(uniq);
        } else {
          setAvailableServices([]);
        }
      } catch (e) {
        console.warn(
          "[ResgateComercial] procedimentos não disponíveis:",
          (e && e.message) || e
        );
      }
    };
    loadProcedures();
    return () => {
      cancelled = true;
    };
  }, []);

  const openDetail = (lead) => {
    setSel(lead);
    setScriptDraft(Array.isArray(lead.script) ? lead.script : []);
    setNotaDraft(lead.notes || "");
    // S45 A8: carrega orçamentos editáveis
    setBudgetsDraft(Array.isArray(lead.budgets) ? lead.budgets : []);
    // S45 A9: carrega datas disponíveis editáveis
    const sd =
      lead.surgeryDates &&
      typeof lead.surgeryDates === "object" &&
      !Array.isArray(lead.surgeryDates)
        ? lead.surgeryDates
        : {};
    setDatesDraft(sd);
    setNewDateInputs({});
    setNewCityName("");
    // S45 A12: carrega valor editável
    setValorDraft(lead.totalValue ? String(lead.totalValue) : "");
    // S45 A14: carrega telefone + procedimento editáveis
    setPhoneDraft(lead.phone || "");
    setProcedureDraft(lead.procedure || "");
    setDetailTab(0); // Fase 3 v1.5: sempre abre na aba Detalhes
  };

  const persist = async (id, patch) => {
    try {
      const updated = await updateRescueLead(id, patch);
      setLeads((prev) => prev.map((x) => (x.id === id ? updated : x)));
      if (sel && sel.id === id) setSel(updated);
      getRescueStats().then((s) => setStats(s || {}));
      return updated;
    } catch (e) {
      console.error("[ResgateComercial] persist", e);
      toast.error("Erro ao salvar. Tente novamente.");
    }
  };

  // Stage muda: se for "perdido", abre o dialog de motivo antes; senao persiste direto.
  const onChangeStage = (lead, stage) => {
    if (stage === "perdido") {
      setLostFor(lead);
      return;
    }
    persist(lead.id, { stage });
  };
  const onChangePrio = (lead, priority) => persist(lead.id, { priority });
  // [S113] atribuir responsável pelo resgate ("" = sem dono)
  const onChangeResponsavel = (lead, userId) =>
    persist(lead.id, { userId: userId === "" ? null : userId });

  // [S113] Abre a conversa DO LEAD dentro do CRM: acha/cria o contato pelo
  // telefone e abre o NewTicketModal (cria/abre o ticket). Substitui o wa.me
  // externo — os números já estão conectados no sistema.
  const handleOpenConversation = async (lead) => {
    const digits = cleanPhone(lead && lead.phone);
    if (!digits) {
      toast.warn("Lead sem telefone — edite pra adicionar.");
      return;
    }
    try {
      if (lead.contactId) {
        setTicketContact({ id: lead.contactId, name: lead.name, number: digits });
        setTicketModalOpen(true);
        return;
      }
      const tail = digits.slice(-8);
      const { data } = await api.get("/contacts", {
        params: { searchParam: tail }
      });
      const found = (data.contacts || []).find((c) =>
        cleanPhone(c.number).endsWith(tail)
      );
      if (found) {
        setTicketContact({ id: found.id, name: found.name, number: found.number });
        setTicketModalOpen(true);
        return;
      }
      // não existe → cria o contato com nome+telefone do lead
      const fullPhone = digits.startsWith("55") ? digits : "55" + digits;
      const { data: created } = await api.post("/contacts", {
        name: lead.name || fullPhone,
        number: fullPhone
      });
      setTicketContact({
        id: created.id,
        name: created.name,
        number: created.number
      });
      setTicketModalOpen(true);
    } catch (e) {
      toast.error("Não consegui abrir a conversa. Tenta de novo.");
    }
  };

  const handleCloseTicketModal = (ticket) => {
    setTicketModalOpen(false);
    setTicketContact(null);
    if (ticket && ticket.uuid) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  // Fase 2 v1.5: quick action "avancar stage" (ligar → contato → agendado → fechado).
  // NAO avanca pra "perdido" (precisa do motivo via LostReasonDialog).
  const advanceStage = (lead) => {
    const cur = lead.stage || "ligar";
    const idx = STAGE_FLOW.indexOf(cur);
    if (idx < 0 || idx >= STAGE_FLOW.length - 1) {
      toast.info("Já está no último stage. Use o menu pra mover.");
      return;
    }
    const next = STAGE_FLOW[idx + 1];
    const nextLabel = STAGES.find((s) => s.key === next).label;
    persist(lead.id, { stage: next });
    toast.success(`${lead.name || "Lead"} → ${nextLabel}`);
  };

  // Fase 2 v1.5: setar nextContactAt do lead via datetime-local
  const onChangeNextContact = (lead, val) => {
    // val pode ser "" (limpar) ou "YYYY-MM-DDTHH:MM" (local).
    const iso = val ? new Date(val).toISOString() : null;
    persist(lead.id, { nextContactAt: iso });
  };

  // Fase 3 v1.5: drag-and-drop entre colunas. result vem do react-beautiful-dnd.
  const onDragEnd = (result) => {
    if (!result.destination) return;
    const { draggableId, source, destination } = result;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }
    const lead = leads.find((l) => String(l.id) === String(draggableId));
    if (!lead) return;
    const newStage = destination.droppableId;
    const newPosition = destination.index;
    // Dropar em "perdido" abre o dialog do motivo (Fase 1 v1.5).
    if (newStage === "perdido" && (lead.stage || "ligar") !== "perdido") {
      setLostFor(lead);
      return;
    }
    persist(lead.id, { stage: newStage, position: newPosition });
  };

  const handleLostConfirm = async (reason) => {
    if (!lostFor) return;
    const updated = await persist(lostFor.id, {
      stage: "perdido",
      lostReason: reason
    });
    setLostFor(null);
    // Fase 4.1 v1.5: se veio do Modo Foco, tambem fecha o foco
    if (focusLead && lostFor.id === focusLead.id) {
      setFocusLead(null);
    }
    if (updated) toast.info(`Lead marcado como perdido (${reason})`);
  };

  const salvarScript = async () => {
    if (!sel) return;
    const u = await persist(sel.id, { script: scriptDraft });
    if (u) toast.success("Script salvo");
  };
  const salvarNota = async () => {
    if (!sel) return;
    const u = await persist(sel.id, { notes: notaDraft });
    if (u) toast.success("Anotação salva");
  };
  // S45 A8: salvar orçamentos. Trim de strings + filter de linhas totalmente vazias.
  const salvarOrcamentos = async () => {
    if (!sel) return;
    const cleaned = (budgetsDraft || [])
      .map((b) => ({
        cidade: (b.cidade || "").trim(),
        cirurgia: (b.cirurgia || "").trim(),
        hospital: (b.hospital || "").trim(),
        total: (b.total || "").trim(),
      }))
      .filter((b) => b.cidade || b.cirurgia || b.hospital || b.total);
    const u = await persist(sel.id, { budgets: cleaned });
    if (u) {
      setBudgetsDraft(cleaned);
      toast.success("Orçamentos salvos");
    }
  };
  const adicionarOrcamento = () => {
    setBudgetsDraft([
      ...(budgetsDraft || []),
      { cidade: "", cirurgia: "", hospital: "", total: "" },
    ]);
  };
  const removerOrcamento = (idx) => {
    const cp = [...(budgetsDraft || [])];
    cp.splice(idx, 1);
    setBudgetsDraft(cp);
  };
  const editarOrcamento = (idx, field, val) => {
    const cp = [...(budgetsDraft || [])];
    cp[idx] = { ...cp[idx], [field]: val };
    setBudgetsDraft(cp);
  };

  // S45 A9: Datas Disponíveis editáveis
  const removerData = (city, idx) => {
    const arr = [...(datesDraft[city] || [])];
    arr.splice(idx, 1);
    setDatesDraft({ ...datesDraft, [city]: arr });
  };
  const adicionarDataNaCidade = (city) => {
    const val = ((newDateInputs[city] || "") + "").trim();
    if (!val) return;
    const arr = [...(datesDraft[city] || []), val];
    setDatesDraft({ ...datesDraft, [city]: arr });
    setNewDateInputs({ ...newDateInputs, [city]: "" });
  };
  // S45 A12: parser BR robusto pra totalValue.
  // Aceita "38100", "R$ 38.100", "R$ 38.100,00", "38.100,50", etc.
  // Sempre retorna integer (totalValue é INTEGER no DB).
  const parseValueBR = (s) => {
    if (typeof s === "number") return Math.round(s) || 0;
    let v = String(s || "").trim().replace(/R\$|\s/gi, "");
    if (!v) return 0;
    if (v.includes(",")) {
      // formato BR com decimais: "38.100,50" -> usa parte inteira
      v = v.split(",")[0].replace(/\./g, "");
    } else {
      // sem decimal: remove pontos separadores de milhar
      v = v.replace(/\./g, "");
    }
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : 0;
  };
  const salvarValor = async () => {
    if (!sel) return;
    const valor = parseValueBR(valorDraft);
    const u = await persist(sel.id, { totalValue: valor });
    if (u) {
      // sincroniza o draft com o que efetivamente foi salvo (já em integer)
      setValorDraft(valor ? String(valor) : "");
      toast.success("Valor salvo");
    }
  };
  // S45 A14: salva telefone (trim simples, sem máscara forçada — preserva formatos)
  const salvarTelefone = async () => {
    if (!sel) return;
    const phone = (phoneDraft || "").trim();
    const u = await persist(sel.id, { phone });
    if (u) {
      setPhoneDraft(phone);
      toast.success("Telefone salvo");
    }
  };
  // S45 A14: salva procedimento (trim)
  const salvarProcedimento = async () => {
    if (!sel) return;
    const procedure = (procedureDraft || "").trim();
    const u = await persist(sel.id, { procedure });
    if (u) {
      setProcedureDraft(procedure);
      toast.success("Procedimento salvo");
    }
  };

  // S45 A11: helper pra formatar Date HTML5 (YYYY-MM-DD) pra "DD/MM/AAAA" BR
  const formatDateBR = (isoLike) => {
    if (!isoLike) return "";
    const [yyyy, mm, dd] = String(isoLike).split("-");
    if (!yyyy || !mm || !dd) return "";
    return `${dd.padStart(2, "0")}/${mm.padStart(2, "0")}/${yyyy}`;
  };
  const removerCidadeDeDatas = (city) => {
    const cp = { ...datesDraft };
    delete cp[city];
    setDatesDraft(cp);
  };
  const adicionarNovaCidadeDatas = () => {
    const name = (newCityName || "").trim();
    if (!name) return;
    if (datesDraft[name] !== undefined) {
      toast.info(`Cidade "${name}" já existe`);
      return;
    }
    setDatesDraft({ ...datesDraft, [name]: [] });
    setNewCityName("");
  };
  const salvarDatas = async () => {
    if (!sel) return;
    // Limpa datas vazias por cidade + remove cidades totalmente vazias
    const cleaned = {};
    Object.entries(datesDraft || {}).forEach(([city, arr]) => {
      const trimmedArr = (arr || [])
        .map((d) => (d || "").trim())
        .filter((d) => d);
      if (trimmedArr.length > 0 || (arr || []).length === 0) {
        // mantém mesmo cidade vazia (caso usuário queira preencher depois)
        cleaned[city] = trimmedArr;
      }
    });
    const u = await persist(sel.id, { surgeryDates: cleaned });
    if (u) {
      setDatesDraft(cleaned);
      toast.success("Datas salvas");
    }
  };

  // Fase 4.2 v1.5: toggle item do checklist pre-cirurgia. Marca/desmarca,
  // grava timestamp do check. backend audita autoria via JWT.
  const toggleChecklistItem = async (lead, idx) => {
    if (!Array.isArray(lead.preopChecklist)) return;
    const updated = lead.preopChecklist.map((it, i) => {
      if (i !== idx) return it;
      const willCheck = !it.checked;
      return {
        ...it,
        checked: willCheck,
        at: willCheck ? new Date().toISOString() : null
      };
    });
    const u = await persist(lead.id, { preopChecklist: updated });
    if (u) {
      const it = updated[idx];
      if (it.checked) {
        toast.success(`✓ ${it.label}`);
      }
    }
  };

  // Fase 4.3 v1.5: toggle tag em um lead (atribui se nao tem; remove se tem).
  // Re-sincroniza tags do lead via API setLeadRescueTags (replace).
  const toggleLeadTag = async (lead, tagId) => {
    const current = Array.isArray(lead.tags)
      ? lead.tags.map((t) => Number(t.id))
      : [];
    const has = current.includes(Number(tagId));
    const next = has
      ? current.filter((id) => Number(id) !== Number(tagId))
      : [...current, Number(tagId)];
    try {
      const updated = await setLeadRescueTags(lead.id, next);
      setLeads((prev) => prev.map((x) => (x.id === lead.id ? updated : x)));
      if (sel && sel.id === lead.id) setSel(updated);
    } catch (e) {
      console.error("[ResgateComercial] toggleLeadTag", e);
      toast.error("Erro ao atualizar tags");
    }
  };

  // Fase 4.3 v1.5: gerenciamento de tags (CRUD). Chamadas pelo dialog.
  const reloadTags = useCallback(async () => {
    try {
      const t = await listRescueTags();
      setTags(Array.isArray(t) ? t : []);
    } catch (e) {
      console.error("[ResgateComercial] reloadTags", e);
    }
  }, []);

  const handleCreateTag = async (name, color) => {
    try {
      await createRescueTag({ name, color });
      await reloadTags();
      toast.success(`Tag "${name}" criada`);
    } catch (e) {
      console.error("[ResgateComercial] createTag", e);
      toast.error("Erro ao criar tag");
    }
  };

  const handleUpdateTag = async (id, patch) => {
    try {
      await updateRescueTag(id, patch);
      await reloadTags();
      // se a tag esta atribuida a leads, atualizar o nome/cor neles tambem
      await reload();
      toast.success("Tag atualizada");
    } catch (e) {
      console.error("[ResgateComercial] updateTag", e);
      toast.error("Erro ao atualizar tag");
    }
  };

  // Fase 4.1 v1.5: Modo Foco da Ligacao — abrir / fechar / acoes rapidas.
  const openFocus = (lead) => {
    setFocusLead(lead);
  };
  const closeFocus = () => {
    setFocusLead(null);
  };

  // Result actions do Modo Foco: aplicam no lead em foco + fecham o painel.
  // - Agendou: stage='agendado' + opcionalmente data
  // - Voltar a ligar: nextContactAt = +1 dia (24h)
  // - Nao atendeu: nota rapida + nextContactAt = +1 dia
  // - Perdido: abre LostReasonDialog (mantem foco ate confirmar)
  const focusResult = async (kind) => {
    if (!focusLead) return;
    const lead = focusLead;
    if (kind === "agendou") {
      await persist(lead.id, { stage: "agendado" });
      toast.success(`${lead.name || "Lead"} → Agendado ✓`);
      closeFocus();
    } else if (kind === "ligar_amanha") {
      const next = new Date(Date.now() + 24 * 3600 * 1000).toISOString();
      await persist(lead.id, { stage: "ligar", nextContactAt: next });
      toast.info(`${lead.name || "Lead"}: re-ligar amanhã`);
      closeFocus();
    } else if (kind === "nao_atendeu") {
      const next = new Date(Date.now() + 4 * 3600 * 1000).toISOString();
      const oldNotes = lead.notes || "";
      const stamp = new Date().toLocaleString("pt-BR");
      const newNotes = oldNotes
        ? `${oldNotes}\n[${stamp}] Não atendeu`
        : `[${stamp}] Não atendeu`;
      await persist(lead.id, { notes: newNotes, nextContactAt: next });
      toast.info(`${lead.name || "Lead"}: não atendeu, retorno em 4h`);
      closeFocus();
    } else if (kind === "perdido") {
      // Mantém foco; LostReasonDialog cuida do resto, fecha foco após confirmar.
      setLostFor(lead);
    }
  };

  const handleDeleteTag = async (tag) => {
    if (
      !window.confirm(
        `Remover a tag "${tag.name}"? Ela será removida de todos os leads vinculados.`
      )
    )
      return;
    try {
      await deleteRescueTag(tag.id);
      await reloadTags();
      await reload(); // leads referentes recarregam sem a tag (cascade)
      toast.info(`Tag "${tag.name}" removida`);
    } catch (e) {
      console.error("[ResgateComercial] deleteTag", e);
      toast.error("Erro ao remover tag");
    }
  };

  const removerLead = async () => {
    if (!sel) return;
    if (!window.confirm(`Remover o lead "${sel.name}"? Esta ação não pode ser desfeita.`))
      return;
    try {
      await deleteRescueLead(sel.id);
      toast.success("Lead removido");
      setSel(null);
      reload();
    } catch (e) {
      console.error(e);
      toast.error("Erro ao remover o lead");
    }
  };

  // Fase 2 v1.5: ordena overdue → today → soon → resto, depois por position.
  const sortLeads = (a, b) => {
    const ba = computeNextBadge(a.nextContactAt);
    const bb = computeNextBadge(b.nextContactAt);
    const pa = ba ? ba.priority : 99;
    const pb = bb ? bb.priority : 99;
    if (pa !== pb) return pa - pb;
    return (a.position || 0) - (b.position || 0);
  };

  const grupos = STAGES.map((st) => ({
    ...st,
    items: leads
      .filter((l) => (l.stage || "ligar") === st.key)
      .sort(sortLeads)
  }));

  // F1.6 v1.5 (S29 — 20/05/2026): Úrsula pediu mensurar "de 99 que entram,
  // quantos fecho". Dividimos os stats em 2 seções narrativas:
  //   1) PERFORMANCE do funil (resultado): leads que entraram, fechadas,
  //      perdidas, taxa de conversão %, receita ganha, ticket médio,
  //      pipeline aberto. Os 2 cards mais importantes (Conversão + Receita)
  //      têm highlight visual (borda colorida + gradiente).
  //   2) OPERAÇÃO do dia (ações imediatas): ligar agora, hoje, esta semana,
  //      em andamento.
  // Os campos do backend permanecem retrocompatíveis (urgente/hoje/semana/
  // total/fechado/receita/pipeline) + novos (perdido/receitaPerdida/
  // emAberto/conversao/ticketMedio).
  const decididos = (stats.fechado || 0) + (stats.perdido || 0);
  const STAT_CARDS_PERFORMANCE = [
    {
      key: "total",
      label: "Leads no Painel",
      value: stats.total || 0,
      sub: "total que entraram",
      tint: "#5e35b1",
      Icon: People
    },
    {
      key: "fechado",
      label: "Cirurgias Fechadas",
      value: stats.fechado || 0,
      sub: `${stats.fechado || 0} de ${decididos} decididos`,
      tint: "#06c270",
      Icon: CheckCircle
    },
    {
      key: "perdido",
      label: "Cirurgias Perdidas",
      value: stats.perdido || 0,
      sub: `${brl(stats.receitaPerdida || 0)} não convertidos`,
      tint: "#9e9e9e",
      Icon: Cancel
    },
    {
      key: "conversao",
      label: "Conversão",
      value: `${stats.conversao || 0}%`,
      sub: "fechadas ÷ decididas",
      tint: "#06c270",
      Icon: TrendingUp,
      highlight: true
    },
    {
      key: "receita",
      label: "Receita Ganha",
      value: brl(stats.receita || 0),
      sub: "valor consolidado",
      tint: "#06c270",
      Icon: AttachMoney,
      highlight: true
    },
    {
      key: "ticketMedio",
      label: "Ticket Médio",
      value: brl(stats.ticketMedio || 0),
      sub: "por cirurgia fechada",
      tint: "#4254cb",
      Icon: Receipt
    },
    {
      key: "pipeline",
      label: "Pipeline Aberto",
      value: brl(stats.pipeline || 0),
      sub: "potencial em negociação",
      tint: "#0288d1",
      Icon: AccountBalance
    }
  ];
  const STAT_CARDS_OPERACAO = [
    {
      key: "urgente",
      label: "Ligar Agora",
      value: stats.urgente || 0,
      tint: "#e53935",
      Icon: PriorityHighRounded
    },
    {
      key: "hoje",
      label: "Ligar Hoje",
      value: stats.hoje || 0,
      tint: "#ff9800",
      Icon: Today
    },
    {
      key: "semana",
      label: "Esta Semana",
      value: stats.semana || 0,
      tint: "#03a9f4",
      Icon: DateRange
    },
    {
      key: "emAberto",
      label: "Em Andamento",
      value: stats.emAberto || 0,
      tint: theme.palette.primary.main,
      Icon: Schedule
    }
  ];

  return (
    <Box className={classes.root}>
      {/* [S113] modal de abrir conversa DENTRO do CRM (substitui wa.me externo) */}
      <NewTicketModal
        modalOpen={ticketModalOpen}
        onClose={handleCloseTicketModal}
        initialContact={ticketContact || undefined}
      />
      {/* HEADER */}
      <Box className={classes.header}>
        <Box className={classes.titleBlock}>
          <Box className={classes.titleIcon}>
            <Healing style={{ fontSize: 26 }} />
          </Box>
          <Box>
            <Typography className={classes.pageTitle}>
              Resgate Comercial
            </Typography>
            <Typography className={classes.pageSubtitle}>
              Funil de vendas · Setor Comercial
            </Typography>
          </Box>
        </Box>
        <Box style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {/* Fase 4.4 v1.5 — Toggle Board / Agenda */}
          <Box className={classes.viewToggle}>
            <Tooltip title="Visualizar Kanban" arrow>
              <IconButton
                size="small"
                onClick={() => setViewMode("board")}
                className={`${classes.viewToggleBtn} ${
                  viewMode === "board" ? classes.viewToggleBtnActive : ""
                }`}
              >
                <Tune style={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Visualizar Agenda" arrow>
              <IconButton
                size="small"
                onClick={() => setViewMode("calendar")}
                className={`${classes.viewToggleBtn} ${
                  viewMode === "calendar" ? classes.viewToggleBtnActive : ""
                }`}
              >
                <EventIcon style={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => setNovoOpen(true)}
            className={classes.primaryAction}
            disableElevation
          >
            Novo Lead
          </Button>
        </Box>
      </Box>

      {/* STATS — SEÇÃO 1: PERFORMANCE DO FUNIL (resultado — Úrsula mensura
          aqui). F1.6 v1.5: 7 cards (Leads, Fechadas, Perdidas, Conversão,
          Receita, Ticket Médio, Pipeline). Conversão e Receita destacadas. */}
      <Box className={classes.statsGrid}>
        <Box className={classes.statsSectionHeader}>
          <Assessment
            style={{
              fontSize: 16,
              color: theme.palette.primary.main
            }}
          />
          <Typography className={classes.statsSectionTitle}>
            Performance do Funil
          </Typography>
          <Box className={classes.statsSectionDivider} />
        </Box>
        <Grid container spacing={2}>
          {STAT_CARDS_PERFORMANCE.map((s) => {
            const Ic = s.Icon;
            const hl = !!s.highlight;
            return (
              <Grid item xs={6} sm={4} md={3} lg key={s.key}>
                <Paper
                  className={`${classes.statCard} ${classes.statCardHighlight}`}
                  elevation={0}
                  data-highlight={hl ? "true" : "false"}
                  style={
                    hl
                      ? {
                          borderColor: `${s.tint}66`,
                          background:
                            theme.palette.type === "dark"
                              ? `linear-gradient(135deg, ${theme.palette.background.paper}, ${s.tint}1c)`
                              : `linear-gradient(135deg, ${theme.palette.background.paper}, ${s.tint}11)`,
                          boxShadow: `0 4px 16px ${s.tint}22`
                        }
                      : undefined
                  }
                >
                  <Box
                    className={classes.statIconWrap}
                    style={{ background: `${s.tint}1f`, color: s.tint }}
                  >
                    <Ic style={{ fontSize: 22 }} />
                  </Box>
                  <Box style={{ minWidth: 0 }}>
                    <Typography
                      className={`${classes.statValue} ${
                        hl ? classes.statValueHighlight : ""
                      }`}
                      style={hl ? { color: s.tint } : undefined}
                    >
                      {s.value}
                    </Typography>
                    <Typography className={classes.statLabel}>
                      {s.label}
                    </Typography>
                    {s.sub && (
                      <Typography className={classes.statSubLabel}>
                        {s.sub}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* STATS — SEÇÃO 2: OPERAÇÃO DO DIA (ações imediatas).
          F1.6 v1.5: 4 cards (Ligar Agora, Ligar Hoje, Esta Semana, Em Andamento). */}
      <Box className={classes.statsGrid}>
        <Box className={classes.statsSectionHeader}>
          <NotificationsActive
            style={{
              fontSize: 16,
              color: theme.palette.primary.main
            }}
          />
          <Typography className={classes.statsSectionTitle}>
            Operação do Dia
          </Typography>
          <Box className={classes.statsSectionDivider} />
        </Box>
        <Grid container spacing={2}>
          {STAT_CARDS_OPERACAO.map((s) => {
            const Ic = s.Icon;
            return (
              <Grid item xs={6} sm={4} md={3} lg key={s.key}>
                <Paper className={classes.statCard} elevation={0}>
                  <Box
                    className={classes.statIconWrap}
                    style={{ background: `${s.tint}1f`, color: s.tint }}
                  >
                    <Ic style={{ fontSize: 22 }} />
                  </Box>
                  <Box style={{ minWidth: 0 }}>
                    <Typography className={classes.statValue}>
                      {s.value}
                    </Typography>
                    <Typography className={classes.statLabel}>
                      {s.label}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* MINI FUNIL — distribuicao por stage */}
      {!loading && <MiniFunnel grupos={grupos} totalLeads={leads.length} theme={theme} classes={classes} />}

      {/* BOARD ou AGENDA — Fase 4.4 v1.5 view toggle */}
      {viewMode === "calendar" && !loading && (
        <CalendarView
          leads={leads}
          classes={classes}
          theme={theme}
          onOpenLead={openDetail}
        />
      )}
      {viewMode === "board" && loading ? (
        <Box className={classes.board}>
          {STAGES.map((g) => (
            <Paper key={g.key} className={classes.column} elevation={0}>
              <Box className={classes.columnHeader}>
                <Skeleton variant="text" width={140} height={18} />
                <Skeleton variant="circle" width={22} height={22} />
              </Box>
              <Box className={classes.columnBody}>
                {[1, 2, 3].map((i) => (
                  <Paper key={i} className={classes.leadCard} elevation={0}>
                    <Box className={classes.leadTop}>
                      <Skeleton variant="circle" width={36} height={36} />
                      <Box style={{ flex: 1 }}>
                        <Skeleton variant="text" width="70%" height={16} />
                        <Skeleton variant="text" width="50%" height={12} />
                      </Box>
                    </Box>
                    <Box className={classes.leadFooter}>
                      <Skeleton variant="rect" width={90} height={22} style={{ borderRadius: 8 }} />
                      <Skeleton variant="text" width={70} height={18} />
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Paper>
          ))}
        </Box>
      ) : viewMode === "board" ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <Box className={classes.board}>
            {grupos.map((g) => {
              const Ic = g.Icon;
              return (
                <Droppable key={g.key} droppableId={g.key}>
                  {(dropProvided, dropSnapshot) => (
                    <Paper
                      className={`${classes.column} ${
                        dropSnapshot.isDraggingOver ? classes.columnDragOver : ""
                      }`}
                      elevation={0}
                      ref={dropProvided.innerRef}
                      {...dropProvided.droppableProps}
                    >
                      <Box className={classes.columnHeader}>
                        <Box className={classes.columnTitle}>
                          <Ic style={{ fontSize: 16, color: theme.palette.primary.main }} />
                          <span>{g.label}</span>
                        </Box>
                        <Box className={classes.columnCount}>{g.items.length}</Box>
                      </Box>
                      <Box className={classes.columnBody}>
                        {g.items.map((l, idx) => {
                          const p = PRIORITIES[l.priority] || PRIORITIES.semana;
                          const PIcon = p.icon;
                          const nb = computeNextBadge(l.nextContactAt);
                          const NBIcon = nb ? nb.Icon : null;
                          return (
                            <Draggable
                              key={l.id}
                              draggableId={String(l.id)}
                              index={idx}
                            >
                              {(dragProvided, dragSnapshot) => (
                                <Paper
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  {...dragProvided.dragHandleProps}
                                  className={`${classes.leadCard} ${classes.leadCardWithActions}`}
                                  elevation={0}
                                  onClick={() => openDetail(l)}
                                  style={{
                                    ...dragProvided.draggableProps.style,
                                    opacity: dragSnapshot.isDragging ? 0.9 : 1,
                                    boxShadow: dragSnapshot.isDragging
                                      ? theme.palette.type === "dark"
                                        ? "0 12px 28px rgba(0,0,0,0.55)"
                                        : "0 12px 28px rgba(0,0,0,0.18)"
                                      : undefined
                                  }}
                                >
                                  {/* Fase 2 v1.5 — Quick actions overlay (hover) */}
                                  <Box
                                    className={classes.quickActions}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Tooltip title="Modo Foco (Ligar)" arrow>
                                      <IconButton size="small" onClick={() => openFocus(l)}>
                                        <Phone style={{ fontSize: 14, color: theme.palette.primary.main }} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Avançar stage" arrow>
                                      <IconButton size="small" onClick={() => advanceStage(l)}>
                                        <ArrowForwardIos style={{ fontSize: 13, color: theme.palette.primary.main }} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Abrir WhatsApp" arrow>
                                      <IconButton size="small" onClick={() => handleOpenConversation(l)}>
                                        <WhatsApp style={{ fontSize: 15, color: "#25D366" }} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Editar / Notas" arrow>
                                      <IconButton size="small" onClick={() => openDetail(l)}>
                                        <EditOutlined style={{ fontSize: 14, color: theme.palette.text.secondary }} />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>

                                  <Box className={classes.leadTop}>
                                    <Avatar className={classes.leadAvatar}>
                                      {initials(l.name)}
                                    </Avatar>
                                    <Box className={classes.leadIdentity}>
                                      <Typography className={classes.leadName}>
                                        {l.name || l.phone || "—"}
                                      </Typography>
                                      <Typography className={classes.leadProcedure}>
                                        {l.procedure || "—"}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  {/* Fase 4.3 v1.5 — Tags do lead (max 3 + overflow) */}
                                  {Array.isArray(l.tags) && l.tags.length > 0 && (
                                    <Box className={classes.cardTagsRow}>
                                      {l.tags.slice(0, 3).map((tg) => (
                                        <span
                                          key={tg.id}
                                          className={classes.cardTagChip}
                                          style={{
                                            backgroundColor: tg.color || "#607d8b"
                                          }}
                                          title={tg.name}
                                        >
                                          {tg.name}
                                        </span>
                                      ))}
                                      {l.tags.length > 3 && (
                                        <span className={classes.cardTagOverflow}>
                                          +{l.tags.length - 3}
                                        </span>
                                      )}
                                    </Box>
                                  )}
                                  <Box className={classes.leadFooter}>
                                    <Box style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
                                      <Chip
                                        size="small"
                                        label={p.label}
                                        icon={<PIcon style={{ fontSize: 14, color: p.tint }} />}
                                        className={classes.priorityChip}
                                        style={{
                                          backgroundColor: `${p.tint}18`,
                                          color: p.tint,
                                          border: `1px solid ${p.tint}30`
                                        }}
                                      />
                                      {/* Fase 2 v1.5 — Next contact badge */}
                                      {nb && (
                                        <span
                                          className={`${classes.nextBadge} ${
                                            nb.kind === "overdue"
                                              ? classes.nextBadgeOverdue
                                              : nb.kind === "today"
                                              ? classes.nextBadgeToday
                                              : classes.nextBadgeSoon
                                          }`}
                                        >
                                          <NBIcon style={{ fontSize: 11 }} />
                                          {nb.label}
                                        </span>
                                      )}
                                    </Box>
                                    <Typography className={classes.leadValue}>
                                      {brl(l.totalValue)}
                                    </Typography>
                                  </Box>
                                </Paper>
                              )}
                            </Draggable>
                          );
                        })}
                        {dropProvided.placeholder}
                        {g.items.length === 0 && (
                          <Box className={classes.emptyColumn}>arraste leads aqui</Box>
                        )}
                      </Box>
                    </Paper>
                  )}
                </Droppable>
              );
            })}
          </Box>
        </DragDropContext>
      ) : null}

      {/* DETAIL DIALOG */}
      <Dialog
        open={Boolean(sel)}
        onClose={() => setSel(null)}
        fullScreen={isMobile}
        fullWidth
        maxWidth="md"
        classes={{ paper: classes.dialogPaper }}
        TransitionComponent={Fade}
      >
        {sel && (
          <>
            <Box className={classes.dialogHeader}>
              <Box className={classes.dialogHeaderIdentity}>
                <Avatar className={classes.dialogAvatar}>
                  {initials(sel.name)}
                </Avatar>
                <Box style={{ minWidth: 0 }}>
                  <Typography className={classes.dialogName}>
                    {sel.name || "—"}
                  </Typography>
                  <Typography className={classes.dialogSubtitle}>
                    {(sel.procedure || "—") + " · " + (sel.origin || "—")}
                  </Typography>
                </Box>
              </Box>
              <IconButton size="small" onClick={() => setSel(null)}>
                <Close />
              </IconButton>
            </Box>

            {/* Fase 3 v1.5 — Tabs Detalhes / Histórico */}
            <Tabs
              value={detailTab}
              onChange={(_, v) => setDetailTab(v)}
              indicatorColor="primary"
              textColor="primary"
              className={classes.tabsRoot}
            >
              <Tab className={classes.tab} label="Detalhes" />
              <Tab className={classes.tab} label="Histórico" />
            </Tabs>

            <DialogContent className={classes.dialogBody}>
              {detailTab === 1 ? (
                <ActivityTimeline
                  leadId={sel.id}
                  classes={classes}
                  theme={theme}
                />
              ) : (
              <>
              {/* Banner motivo da perda (Fase 1 v1.5) */}
              {sel.stage === "perdido" && sel.lostReason && (
                <Box className={classes.lostBanner}>
                  <Cancel style={{ fontSize: 20, color: theme.palette.error.main }} />
                  <Box style={{ flex: 1 }}>
                    <Typography style={{ fontSize: "0.6875rem", fontWeight: 700, color: theme.palette.error.main, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      Lead perdido
                    </Typography>
                    <Typography style={{ fontSize: "0.875rem", color: theme.palette.text.primary, marginTop: 2 }}>
                      Motivo: <strong>{sel.lostReason}</strong>
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Meta grid */}
              <Grid container spacing={2} className={classes.metaGrid}>
                <Grid item xs={12} sm={6}>
                  <FormControl variant="outlined" fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={sel.stage || "ligar"}
                      label="Status"
                      onChange={(e) => onChangeStage(sel, e.target.value)}
                    >
                      {STAGES.map((s) => (
                        <MenuItem key={s.key} value={s.key}>
                          {s.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl variant="outlined" fullWidth size="small">
                    <InputLabel>Prioridade</InputLabel>
                    <Select
                      value={sel.priority || "semana"}
                      label="Prioridade"
                      onChange={(e) => onChangePrio(sel, e.target.value)}
                    >
                      {Object.entries(PRIORITIES).map(([k, p]) => (
                        <MenuItem key={k} value={k}>
                          {p.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {/* [S113] Responsável pelo resgate — atribuição por membro da equipe */}
                <Grid item xs={12}>
                  <FormControl variant="outlined" fullWidth size="small">
                    <InputLabel>Responsável pelo resgate</InputLabel>
                    <Select
                      value={sel.userId || ""}
                      label="Responsável pelo resgate"
                      onChange={(e) => onChangeResponsavel(sel, e.target.value)}
                    >
                      <MenuItem value="">
                        <em>— Sem responsável —</em>
                      </MenuItem>
                      {teamUsers.map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {/* Fase 2 v1.5 — Próximo contato (datetime-local) */}
                <Grid item xs={12}>
                  <Typography className={classes.metaLabel}>
                    Próximo contato (gera badge HOJE/ATRASADO no card)
                  </Typography>
                  <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      type="datetime-local"
                      className={classes.nextContactInput}
                      value={toDatetimeLocal(sel.nextContactAt)}
                      onChange={(e) => onChangeNextContact(sel, e.target.value)}
                      style={{ flex: 1 }}
                    />
                    {sel.nextContactAt && (
                      <Tooltip title="Limpar próximo contato">
                        <IconButton
                          size="small"
                          onClick={() => onChangeNextContact(sel, "")}
                        >
                          <Close style={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  {(() => {
                    const nb = computeNextBadge(sel.nextContactAt);
                    if (!nb) return null;
                    const NBIcon = nb.Icon;
                    return (
                      <Box style={{ marginTop: 6 }}>
                        <span
                          className={`${classes.nextBadge} ${
                            nb.kind === "overdue"
                              ? classes.nextBadgeOverdue
                              : nb.kind === "today"
                              ? classes.nextBadgeToday
                              : classes.nextBadgeSoon
                          }`}
                        >
                          <NBIcon style={{ fontSize: 11 }} />
                          {nb.label}
                        </span>
                      </Box>
                    );
                  })()}
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography className={classes.metaLabel}>Profissão</Typography>
                  <Typography className={classes.metaValue}>
                    {sel.profession || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography className={classes.metaLabel}>Consulta</Typography>
                  <Typography className={classes.metaValue}>
                    {sel.consultDate || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography className={classes.metaLabel}>Próximo passo</Typography>
                  <Typography className={classes.metaValue}>
                    {sel.nextStep || "—"}
                  </Typography>
                </Grid>
                {/* S45 A14 — Telefone editável */}
                <Grid item xs={12} sm={6}>
                  <Typography className={classes.metaLabel}>
                    Telefone{" "}
                    {sel.phone ? (
                      <span style={{ color: theme.palette.text.secondary }}>
                        — atual: {sel.phone}
                      </span>
                    ) : null}
                  </Typography>
                  <Box
                    style={{
                      display: "flex",
                      gap: theme.spacing(0.75),
                      alignItems: "center",
                    }}
                  >
                    <TextField
                      size="small"
                      variant="outlined"
                      placeholder="+55 11 9xxxx-xxxx"
                      value={phoneDraft}
                      onChange={(e) => setPhoneDraft(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          salvarTelefone();
                        }
                      }}
                      style={{ flex: 1, maxWidth: 220 }}
                      inputProps={{
                        style: { fontSize: "0.8125rem" },
                        inputMode: "tel",
                      }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Save />}
                      onClick={salvarTelefone}
                      className={classes.saveBtn}
                      disableElevation
                    >
                      Salvar
                    </Button>
                  </Box>
                </Grid>
                {/* S45 A14 — Procedimento editável (Autocomplete com sugestões do tenant) */}
                <Grid item xs={12} sm={6}>
                  <Typography className={classes.metaLabel}>
                    Procedimento{" "}
                    {sel.procedure ? (
                      <span style={{ color: theme.palette.text.secondary }}>
                        — atual: {sel.procedure}
                      </span>
                    ) : null}
                  </Typography>
                  <Box
                    style={{
                      display: "flex",
                      gap: theme.spacing(0.75),
                      alignItems: "center",
                    }}
                  >
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={availableServices}
                      value={procedureDraft}
                      onChange={(e, newVal) => setProcedureDraft(newVal || "")}
                      onInputChange={(e, newVal) => setProcedureDraft(newVal || "")}
                      style={{ flex: 1, maxWidth: 320 }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="Ex: Avanço Capilar"
                          inputProps={{
                            ...params.inputProps,
                            style: { fontSize: "0.8125rem" },
                          }}
                        />
                      )}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Save />}
                      onClick={salvarProcedimento}
                      className={classes.saveBtn}
                      disableElevation
                    >
                      Salvar
                    </Button>
                  </Box>
                </Grid>
                {/* S45 A12 — Valor (totalValue) editável */}
                <Grid item xs={12} sm={6}>
                  <Typography className={classes.metaLabel}>
                    Valor (R$){" "}
                    {sel.totalValue ? (
                      <span style={{ color: theme.palette.text.secondary }}>
                        — atual: {brl(sel.totalValue)}
                      </span>
                    ) : null}
                  </Typography>
                  <Box
                    style={{
                      display: "flex",
                      gap: theme.spacing(0.75),
                      alignItems: "center",
                    }}
                  >
                    <TextField
                      size="small"
                      variant="outlined"
                      placeholder="Ex: 38100 ou R$ 38.100"
                      value={valorDraft}
                      onChange={(e) => setValorDraft(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          salvarValor();
                        }
                      }}
                      style={{ flex: 1, maxWidth: 220 }}
                      inputProps={{
                        style: { fontSize: "0.8125rem" },
                        inputMode: "numeric",
                      }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Save />}
                      onClick={salvarValor}
                      className={classes.saveBtn}
                      disableElevation
                    >
                      Salvar
                    </Button>
                  </Box>
                </Grid>
                {sel.analysis && (
                  <Grid item xs={12}>
                    <Typography className={classes.metaLabel}>Análise do lead</Typography>
                    <Typography
                      className={classes.metaValue}
                      style={{ lineHeight: 1.6 }}
                    >
                      {sel.analysis}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {/* Fase 5 v1.6 — Tags do lead: SO atribuidas + botao "+" abre popover com disponiveis */}
              <Box className={classes.sectionBlock}>
                <Box className={classes.tagsHeader}>
                  <Typography className={classes.sectionTitle}>
                    <LocalOffer style={{ fontSize: 16 }} />
                    Tags
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<Tune style={{ fontSize: 14 }} />}
                    onClick={() => setTagDialogOpen(true)}
                    className={classes.manageTagsBtn}
                  >
                    Gerenciar
                  </Button>
                </Box>
                {tags.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ fontSize: "0.8125rem" }}
                  >
                    Nenhuma tag criada ainda. Clique em "Gerenciar" pra adicionar.
                  </Typography>
                ) : (() => {
                  const assignedIds = Array.isArray(sel.tags)
                    ? sel.tags.map((t) => Number(t.id))
                    : [];
                  const assignedTags = assignedIds
                    .map((id) => tags.find((tg) => Number(tg.id) === id))
                    .filter(Boolean);
                  const availableTags = tags.filter(
                    (tg) => !assignedIds.includes(Number(tg.id))
                  );
                  return (
                    <Box className={classes.tagsPickerRow}>
                      {assignedTags.length === 0 && (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          style={{ fontSize: "0.8125rem", marginRight: 6, alignSelf: "center" }}
                        >
                          Nenhuma atribuída.
                        </Typography>
                      )}
                      {assignedTags.map((tg) => (
                        <span
                          key={tg.id}
                          className={classes.assignedTagChip}
                          style={{
                            backgroundColor: tg.color || "#607d8b"
                          }}
                          onClick={() => toggleLeadTag(sel, tg.id)}
                          title="Clique pra remover"
                        >
                          {tg.name}
                          <Close className={classes.tagCloseIcon} />
                        </span>
                      ))}
                      {availableTags.length > 0 && (
                        <span
                          className={classes.addTagButton}
                          onClick={(e) => setTagPopoverAnchor(e.currentTarget)}
                          title="Adicionar tag"
                        >
                          <Add style={{ fontSize: 14, marginRight: 2 }} />
                          Adicionar
                        </span>
                      )}
                    </Box>
                  );
                })()}
              </Box>

              {/* Fase 4.2 v1.5 — Checklist pré-cirurgia (auto-popula no agendado) */}
              {Array.isArray(sel.preopChecklist) && sel.preopChecklist.length > 0 && (() => {
                const total = sel.preopChecklist.length;
                const done = sel.preopChecklist.filter((i) => i.checked).length;
                const pct = total > 0 ? (done / total) * 100 : 0;
                const allDone = total > 0 && done === total;
                return (
                  <Box className={classes.sectionBlock}>
                    <Box className={classes.checklistHeader}>
                      <Typography className={classes.sectionTitle}>
                        <AssignmentTurnedIn style={{ fontSize: 16 }} />
                        Checklist Pré-Cirurgia
                      </Typography>
                      <Box className={classes.checklistProgressWrap}>
                        <Typography
                          className={classes.checklistCount}
                          style={{
                            color: allDone
                              ? theme.palette.success.main
                              : theme.palette.text.secondary
                          }}
                        >
                          {done} / {total}
                        </Typography>
                        <Box className={classes.checklistBarOuter}>
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            classes={{
                              root: classes.checklistBar,
                              bar: allDone ? classes.checklistBarFillDone : classes.checklistBarFill
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                    {sel.preopChecklist.map((item, idx) => (
                      <Box
                        key={item.key || idx}
                        className={
                          item.checked
                            ? `${classes.checklistItem} ${classes.checklistItemDone}`
                            : classes.checklistItem
                        }
                        onClick={() => toggleChecklistItem(sel, idx)}
                      >
                        <Checkbox
                          size="small"
                          checked={!!item.checked}
                          icon={<CheckBoxOutlineBlank style={{ fontSize: 20 }} />}
                          checkedIcon={
                            <CheckBoxIcon
                              style={{ fontSize: 20, color: theme.palette.primary.main }}
                            />
                          }
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleChecklistItem(sel, idx)}
                          className={classes.checklistCheckbox}
                        />
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            className={
                              item.checked
                                ? classes.checklistLabelDone
                                : classes.checklistLabel
                            }
                          >
                            {item.label}
                          </Typography>
                          {item.checked && item.at && (
                            <Typography className={classes.checklistAt}>
                              ✓ {new Date(item.at).toLocaleString("pt-BR")}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                );
              })()}

              {/* Orçamentos (S45 A8: editável) */}
              <Box className={classes.sectionBlock}>
                <Typography className={classes.sectionTitle}>
                  <AttachMoney style={{ fontSize: 16 }} />
                  Orçamentos (editável)
                </Typography>
                {(budgetsDraft || []).length === 0 && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ fontSize: "0.8125rem" }}
                  >
                    Sem orçamentos. Clique em "+ Adicionar Orçamento" para cadastrar.
                  </Typography>
                )}
                {(budgetsDraft || []).map((o, i) => (
                  <Box
                    key={i}
                    style={{
                      padding: theme.spacing(1.25, 0),
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Box
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: theme.spacing(1),
                        marginBottom: theme.spacing(0.75),
                      }}
                    >
                      <TextField
                        label="Cidade"
                        size="small"
                        variant="outlined"
                        fullWidth
                        value={o.cidade || ""}
                        onChange={(e) =>
                          editarOrcamento(i, "cidade", e.target.value)
                        }
                        placeholder="Ex.: 🌴 Natal"
                      />
                      <TextField
                        label="Total"
                        size="small"
                        variant="outlined"
                        fullWidth
                        value={o.total || ""}
                        onChange={(e) =>
                          editarOrcamento(i, "total", e.target.value)
                        }
                        placeholder="Ex.: R$ 38.100"
                      />
                    </Box>
                    <Box
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: theme.spacing(1),
                      }}
                    >
                      <TextField
                        label="Cirurgia"
                        size="small"
                        variant="outlined"
                        fullWidth
                        value={o.cirurgia || ""}
                        onChange={(e) =>
                          editarOrcamento(i, "cirurgia", e.target.value)
                        }
                        placeholder="Ex.: R$ 33.600"
                      />
                      <TextField
                        label="Hospital"
                        size="small"
                        variant="outlined"
                        fullWidth
                        value={o.hospital || ""}
                        onChange={(e) =>
                          editarOrcamento(i, "hospital", e.target.value)
                        }
                        placeholder="Ex.: R$ 4.500"
                      />
                    </Box>
                    <Box
                      style={{
                        marginTop: theme.spacing(0.75),
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        size="small"
                        startIcon={<DeleteOutline />}
                        onClick={() => removerOrcamento(i)}
                        style={{
                          color: theme.palette.error.main,
                          fontSize: "0.75rem",
                          textTransform: "none",
                        }}
                      >
                        Remover
                      </Button>
                    </Box>
                  </Box>
                ))}
                <Box
                  style={{
                    marginTop: theme.spacing(1.5),
                    display: "flex",
                    gap: theme.spacing(1),
                    flexWrap: "wrap",
                  }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={adicionarOrcamento}
                    disableElevation
                    style={{ textTransform: "none" }}
                  >
                    + Adicionar Orçamento
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Save />}
                    onClick={salvarOrcamentos}
                    className={classes.saveBtn}
                    disableElevation
                  >
                    Salvar Orçamentos
                  </Button>
                </Box>
              </Box>

              {/* Datas (S45 A9: editável) */}
              <Box className={classes.sectionBlock}>
                <Typography className={classes.sectionTitle}>
                  <EventAvailable style={{ fontSize: 16 }} />
                  Datas Disponíveis (editável)
                </Typography>
                {Object.keys(datesDraft || {}).length === 0 && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ fontSize: "0.8125rem" }}
                  >
                    Sem datas. Adicione uma cidade abaixo para começar.
                  </Typography>
                )}
                {Object.entries(datesDraft || {}).map(([cid, arr]) => (
                  <Box key={cid} className={classes.dateGroup}>
                    <Box
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: theme.spacing(0.5),
                      }}
                    >
                      <Typography className={classes.dateGroupLabel}>
                        {cid}
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => removerCidadeDeDatas(cid)}
                        style={{
                          color: theme.palette.error.main,
                          fontSize: "0.6875rem",
                          textTransform: "none",
                          minWidth: 0,
                          padding: "2px 6px",
                        }}
                      >
                        Remover cidade
                      </Button>
                    </Box>
                    <Box className={classes.dateChips}>
                      {(arr || []).map((d, i) => (
                        <Chip
                          key={i}
                          size="small"
                          label={d}
                          className={classes.dateChip}
                          onDelete={() => removerData(cid, i)}
                        />
                      ))}
                    </Box>
                    <Box
                      style={{
                        marginTop: theme.spacing(0.75),
                        display: "flex",
                        gap: theme.spacing(0.75),
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Nova data (ex: 23/07/2026)"
                        value={newDateInputs[cid] || ""}
                        onChange={(e) =>
                          setNewDateInputs({
                            ...newDateInputs,
                            [cid]: e.target.value,
                          })
                        }
                        onKeyPress={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            adicionarDataNaCidade(cid);
                          }
                        }}
                        style={{ flex: 1, maxWidth: 240, minWidth: 140 }}
                        inputProps={{ style: { fontSize: "0.8125rem" } }}
                      />
                      {/* S45 A10: mini calendário nativo HTML5 — preenche o TextField */}
                      <input
                        type="date"
                        title="Escolher do calendário"
                        onChange={(e) => {
                          const formatted = formatDateBR(e.target.value);
                          if (formatted) {
                            setNewDateInputs({
                              ...newDateInputs,
                              [cid]: formatted,
                            });
                          }
                        }}
                        style={{
                          fontSize: "0.8125rem",
                          padding: "7px 8px",
                          borderRadius: 4,
                          border: `1px solid ${theme.palette.divider}`,
                          background: theme.palette.background.paper,
                          color: theme.palette.text.primary,
                          colorScheme:
                            theme.palette.type === "dark" ? "dark" : "light",
                          cursor: "pointer",
                          width: 150,
                        }}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => adicionarDataNaCidade(cid)}
                        style={{
                          textTransform: "none",
                          fontSize: "0.75rem",
                        }}
                      >
                        Adicionar
                      </Button>
                    </Box>
                  </Box>
                ))}
                {/* Linha pra adicionar nova cidade + salvar */}
                <Box
                  style={{
                    marginTop: theme.spacing(1.5),
                    paddingTop: theme.spacing(1),
                    borderTop: `1px dashed ${theme.palette.divider}`,
                    display: "flex",
                    gap: theme.spacing(0.75),
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Nova cidade (ex: Natal, SP, Brasília)"
                    value={newCityName}
                    onChange={(e) => setNewCityName(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        adicionarNovaCidadeDatas();
                      }
                    }}
                    style={{ flex: 1, maxWidth: 240 }}
                    inputProps={{ style: { fontSize: "0.8125rem" } }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={adicionarNovaCidadeDatas}
                    style={{ textTransform: "none" }}
                  >
                    Nova Cidade
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Save />}
                    onClick={salvarDatas}
                    className={classes.saveBtn}
                    disableElevation
                  >
                    Salvar Datas
                  </Button>
                </Box>
              </Box>

              {/* Script editável */}
              <Box className={classes.sectionBlock}>
                <Typography className={classes.sectionTitle}>
                  <RecordVoiceOver style={{ fontSize: 16 }} />
                  Script de Ligação (editável)
                </Typography>
                {scriptDraft.length === 0 && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ fontSize: "0.8125rem" }}
                  >
                    Sem script. Edite abaixo e salve.
                  </Typography>
                )}
                {scriptDraft.map((s, i) => (
                  <Box key={i} className={classes.scriptItem}>
                    <Box className={classes.scriptStep}>
                      <Box className={classes.scriptNumber}>{i + 1}</Box>
                      <span>{s.titulo || "Etapa"}</span>
                    </Box>
                    <TextField
                      multiline
                      minRows={3}
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={s.fala || ""}
                      onChange={(e) => {
                        const cp = [...scriptDraft];
                        cp[i] = { ...cp[i], fala: e.target.value };
                        setScriptDraft(cp);
                      }}
                    />
                    {s.nota && (
                      <Box className={classes.scriptNote}>💡 {s.nota}</Box>
                    )}
                  </Box>
                ))}
                <Box style={{ marginTop: theme.spacing(1.5) }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Save />}
                    onClick={salvarScript}
                    className={classes.saveBtn}
                    disableElevation
                  >
                    Salvar Script
                  </Button>
                </Box>
              </Box>

              {/* Objeções */}
              {Array.isArray(sel.objections) && sel.objections.length > 0 && (
                <Box className={classes.sectionBlock}>
                  <Typography className={classes.sectionTitle}>
                    <MoreHoriz style={{ fontSize: 16 }} />
                    Objeções e Respostas
                  </Typography>
                  {sel.objections.map((o, i) => (
                    <Box key={i} className={classes.objectionItem}>
                      <Typography className={classes.objectionLabel}>
                        Se disser: "{o.obj}"
                      </Typography>
                      <Typography className={classes.objectionResp}>
                        "{o.resp}"
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Anotações */}
              <Box className={classes.sectionBlock}>
                <Typography className={classes.sectionTitle}>
                  <Notes style={{ fontSize: 16 }} />
                  Anotações da Ligação
                </Typography>
                <TextField
                  multiline
                  minRows={3}
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={notaDraft}
                  onChange={(e) => setNotaDraft(e.target.value)}
                  placeholder="O que aconteceu na ligação, objeções, próximos passos…"
                />
                <Box style={{ marginTop: theme.spacing(1.5) }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Save />}
                    onClick={salvarNota}
                    className={classes.saveBtn}
                    disableElevation
                  >
                    Salvar Anotação
                  </Button>
                </Box>
              </Box>
              </>
              )}
            </DialogContent>

            <DialogActions className={classes.dialogActionsRow}>
              <Box style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DeleteOutline />}
                  onClick={removerLead}
                  className={classes.deleteBtn}
                >
                  Remover
                </Button>
                {/* Fase 4.1 v1.5 — Modo Foco da Ligação (fullscreen) */}
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Phone />}
                  onClick={() => {
                    openFocus(sel);
                    setSel(null);
                  }}
                  className={classes.focusBtn}
                  disableElevation
                >
                  Modo Foco
                </Button>
                {/* Fase 2 v1.5 — Abrir WhatsApp com template do stage atual */}
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<WhatsApp />}
                  onClick={() => handleOpenConversation(sel)}
                  className={classes.waButton}
                  disableElevation
                >
                  Abrir WhatsApp
                </Button>
              </Box>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setSel(null)}
                disableElevation
              >
                Fechar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* NOVO LEAD DIALOG */}
      <NovoLeadDialog
        open={novoOpen}
        onClose={() => setNovoOpen(false)}
        onSaved={() => {
          setNovoOpen(false);
          reload();
        }}
        classes={classes}
        theme={theme}
        availableServices={availableServices}
      />

      {/* LOST REASON DIALOG (Fase 1 v1.5) */}
      <LostReasonDialog
        open={Boolean(lostFor)}
        lead={lostFor}
        onClose={() => setLostFor(null)}
        onConfirm={handleLostConfirm}
        classes={classes}
        theme={theme}
      />

      {/* MANAGE TAGS DIALOG (Fase 4.3 v1.5) */}
      <ManageTagsDialog
        open={tagDialogOpen}
        onClose={() => setTagDialogOpen(false)}
        tags={tags}
        onCreate={handleCreateTag}
        onUpdate={handleUpdateTag}
        onDelete={handleDeleteTag}
        classes={classes}
        theme={theme}
      />

      {/* POPOVER ATRIBUIR TAG (Fase 5 v1.6) — so mostra tags disponiveis pro lead atual */}
      <Popover
        open={Boolean(tagPopoverAnchor)}
        anchorEl={tagPopoverAnchor}
        onClose={() => setTagPopoverAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          elevation: 8,
          style: { borderRadius: 12 }
        }}
      >
        <Box className={classes.tagPopoverWrapper}>
          <Typography className={classes.tagPopoverTitle}>
            <LocalOffer style={{ fontSize: 12 }} />
            Atribuir tag ao lead
          </Typography>
          <Box className={classes.tagPopoverGrid}>
            {(() => {
              if (!sel) return null;
              const assignedIds = Array.isArray(sel.tags)
                ? sel.tags.map((t) => Number(t.id))
                : [];
              const available = tags.filter(
                (tg) => !assignedIds.includes(Number(tg.id))
              );
              if (available.length === 0) {
                return (
                  <Typography className={classes.tagPopoverEmpty}>
                    Todas as tags ja atribuidas.
                  </Typography>
                );
              }
              return available.map((tg) => (
                <span
                  key={tg.id}
                  className={classes.availableTagChip}
                  style={{
                    backgroundColor: `${tg.color || "#607d8b"}1F`,
                    color: tg.color || "#607d8b",
                    border: `1px solid ${tg.color || "#607d8b"}55`
                  }}
                  onClick={() => toggleLeadTag(sel, tg.id)}
                  title={`Atribuir "${tg.name}"`}
                >
                  {tg.name}
                </span>
              ));
            })()}
          </Box>
        </Box>
      </Popover>

      {/* MODO FOCO (Fase 4.1 v1.5) — fullscreen view durante a ligacao */}
      <FocusModeDialog
        open={Boolean(focusLead)}
        lead={focusLead}
        onClose={closeFocus}
        onResult={focusResult}
        onWhatsApp={() => focusLead && handleOpenConversation(focusLead)}
        classes={classes}
        theme={theme}
      />
    </Box>
  );
};

// ---------- NOVO LEAD DIALOG ----------

const NovoLeadDialog = ({
  open,
  onClose,
  onSaved,
  classes,
  theme,
  availableServices = [],
}) => {
  const isMobile = useIsMobile();
  const [f, setF] = useState({
    name: "",
    phone: "",
    procedure: "",
    origin: "",
    profession: "",
    consultDate: "",
    priority: "urgente",
    totalValue: "",
    orcDesc: "",
    analysis: "",
    nextStep: "",
    s1: "",
    s2: "",
    s3: "",
    obj: ""
  });
  const [saving, setSaving] = useState(false);
  // S52 FIX (Úrsula/tela branca): capturar o valor ANTES do updater do setState.
  // Ler e.target.value DENTRO do updater quebra com o pooling de eventos do React
  // (o evento é liberado antes do updater rodar → e.target = null → "Cannot read
  // properties of null (reading 'value')" → tela branca ao digitar).
  const ch = (k) => (e) => {
    const value = e.target.value;
    setF((prev) => ({ ...prev, [k]: value }));
  };

  const salvar = async () => {
    if (!f.name && !f.phone) {
      toast.warn("Informe pelo menos nome ou telefone");
      return;
    }
    // S45 A13: parser robusto + try/catch defensivo + finally pra resetar saving
    const valor = parseInt(String(f.totalValue || "").replace(/\D/g, ""), 10) || 0;
    const script = [];
    if (f.s1) script.push({ titulo: "Abertura", fala: f.s1 });
    if (f.s2) script.push({ titulo: "Argumento Principal", fala: f.s2 });
    if (f.s3) script.push({ titulo: "Fechamento", fala: f.s3 });
    const objections = [];
    if (f.obj) {
      const p = String(f.obj).split("→");
      objections.push({
        obj: (p[0] || f.obj).trim(),
        resp: (p[1] || "—").trim()
      });
    }
    const budgets = [];
    if (f.orcDesc && valor) {
      budgets.push({
        cidade: f.orcDesc,
        cirurgia: "—",
        hospital: "—",
        total: brl(valor)
      });
    }
    const payload = {
      name: (f.name || f.phone || "").trim() || "(sem nome)",
      phone: (f.phone || "").trim(),
      procedure: (f.procedure || "").trim(),
      origin: (f.origin || "").trim(),
      profession: (f.profession || "").trim(),
      consultDate: (f.consultDate || "").trim(),
      nextStep: (f.nextStep || "").trim(),
      priority: f.priority || "semana",
      stage: "ligar",
      totalValue: valor,
      analysis: (f.analysis || "").trim(),
      budgets,
      surgeryDates: {},
      script,
      objections
    };
    setSaving(true);
    try {
      console.log("[NovoLead] payload:", payload);
      await createRescueLead(payload);
      toast.success("Lead criado com sucesso");
      // S45 A13: chama onSaved DENTRO de try — se reload falhar, cai no catch
      try {
        onSaved();
      } catch (cbErr) {
        console.error("[NovoLead] onSaved cb failed:", cbErr);
      }
    } catch (e) {
      console.error("[NovoLead] save failed:", e);
      const apiMsg =
        (e && e.response && e.response.data && e.response.data.error) ||
        (e && e.message) ||
        "Erro desconhecido";
      toast.error(`Erro ao salvar o lead: ${apiMsg}`);
    } finally {
      // Sempre reseta saving — evita botão preso em "Salvando..." se reload async travar
      setSaving(false);
    }
  };

  const F = ({ label, k, ph, multi }) => (
    <TextField
      label={label}
      placeholder={ph}
      value={f[k]}
      onChange={ch(k)}
      fullWidth
      variant="outlined"
      size="small"
      multiline={!!multi}
      minRows={multi ? 2 : undefined}
    />
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="md"
      classes={{ paper: classes.dialogPaper }}
      TransitionComponent={Fade}
    >
      <Box className={classes.dialogHeader}>
        <Box className={classes.dialogHeaderIdentity}>
          <Box
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: theme.palette.primary.contrastText,
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <Add />
          </Box>
          <Box>
            <Typography className={classes.dialogName}>Novo Lead</Typography>
            <Typography className={classes.dialogSubtitle}>
              Cadastre um novo paciente no resgate comercial
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <Close />
        </IconButton>
      </Box>

      <DialogContent className={classes.dialogBody}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField label="Nome completo" placeholder="Ex: Maria Silva" value={f.name} onChange={ch("name")} fullWidth variant="outlined" size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Telefone" placeholder="+55 11 9xxxx-xxxx" value={f.phone} onChange={ch("phone")} fullWidth variant="outlined" size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            {/* S45 A14: Procedimento via Autocomplete free-solo (sugestões do tenant) */}
            <Autocomplete
              freeSolo
              size="small"
              options={availableServices}
              value={f.procedure}
              onChange={(e, newVal) =>
                setF((prev) => ({ ...prev, procedure: newVal || "" }))
              }
              onInputChange={(e, newVal) =>
                setF((prev) => ({ ...prev, procedure: newVal || "" }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Procedimento"
                  placeholder="Ex: Avanço Capilar"
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Origem / Cidade" placeholder="Ex: São Paulo / SP" value={f.origin} onChange={ch("origin")} fullWidth variant="outlined" size="small" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Profissão" placeholder="Ex: Empresária" value={f.profession} onChange={ch("profession")} fullWidth variant="outlined" size="small" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Data da consulta" placeholder="Ex: 18/05/2026" value={f.consultDate} onChange={ch("consultDate")} fullWidth variant="outlined" size="small" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl variant="outlined" fullWidth size="small">
              <InputLabel>Prioridade</InputLabel>
              <Select label="Prioridade" value={f.priority} onChange={ch("priority")}>
                {Object.entries(PRIORITIES).map(([k, p]) => (
                  <MenuItem key={k} value={k}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Valor total (R$)" placeholder="Ex: 38100" value={f.totalValue} onChange={ch("totalValue")} fullWidth variant="outlined" size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Descrição do orçamento" placeholder="Ex: Natal — Cirurgia R$33.600 + Hosp." value={f.orcDesc} onChange={ch("orcDesc")} fullWidth variant="outlined" size="small" />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Análise do lead" placeholder="Comportamento, intenção, pontos de atenção…" value={f.analysis} onChange={ch("analysis")} fullWidth variant="outlined" size="small" multiline minRows={2} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Próximo passo / quando ligar" placeholder="Ex: Ligar amanhã cedo" value={f.nextStep} onChange={ch("nextStep")} fullWidth variant="outlined" size="small" />
          </Grid>
          <Grid item xs={12}>
            <Divider style={{ margin: `${theme.spacing(1)}px 0` }} />
            <Typography className={classes.sectionTitle}>
              <RecordVoiceOver style={{ fontSize: 16 }} />
              Script (opcional — pode editar depois)
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField label="1. Abertura" placeholder="Como abrir a ligação…" value={f.s1} onChange={ch("s1")} fullWidth variant="outlined" size="small" multiline minRows={2} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="2. Argumento principal" placeholder="Ponto central da conversa…" value={f.s2} onChange={ch("s2")} fullWidth variant="outlined" size="small" multiline minRows={2} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="3. Fechamento" placeholder="Como conduzir ao fechamento…" value={f.s3} onChange={ch("s3")} fullWidth variant="outlined" size="small" multiline minRows={2} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Objeção esperada → Resposta (separe com →)" placeholder="Preciso pensar → Claro, mas o encaixe não fica aberto…" value={f.obj} onChange={ch("obj")} fullWidth variant="outlined" size="small" multiline minRows={2} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions className={classes.dialogActionsRow}>
        <Button variant="outlined" onClick={onClose} size="small">
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={salvar}
          disabled={saving}
          startIcon={<Add />}
          disableElevation
        >
          {saving ? "Salvando…" : "Salvar Lead"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------- MINI FUNNEL (Fase 1 v1.5) ----------
// Barra horizontal segmentada por stage, proporcional ao count.
// Mostra o funil de conversao de relance.

const MiniFunnel = ({ grupos, totalLeads, theme, classes }) => {
  if (!totalLeads) {
    return (
      <Box className={classes.miniFunnel}>
        <Typography className={classes.miniFunnelTitle}>
          <TrendingUp style={{ fontSize: 14 }} /> Funil
        </Typography>
        <Typography className={classes.miniFunnelEmpty}>
          Nenhum lead ainda — comece adicionando um.
        </Typography>
      </Box>
    );
  }
  return (
    <Box className={classes.miniFunnel}>
      <Typography className={classes.miniFunnelTitle}>
        <TrendingUp style={{ fontSize: 14, color: theme.palette.primary.main }} />
        Funil de Conversão
      </Typography>
      <Box className={classes.miniFunnelBar}>
        {grupos.map((g) => {
          if (!g.items || g.items.length === 0) return null;
          return (
            <Tooltip
              key={g.key}
              title={`${g.label}: ${g.items.length} (${Math.round((g.items.length / totalLeads) * 100)}%)`}
              arrow
            >
              <Box
                className={classes.miniFunnelSegment}
                style={{
                  flex: g.items.length,
                  background: g.tint
                }}
              >
                {g.items.length}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};

// ---------- LOST REASON DIALOG (Fase 1 v1.5) ----------
// Aparece quando o stage muda pra "perdido". Pede o motivo (radio + textarea
// pra "Outro"). Confirma -> grava lostReason no backend.

const LostReasonDialog = ({ open, lead, onClose, onConfirm, classes, theme }) => {
  const isMobile = useIsMobile();
  const [selected, setSelected] = useState("valor");
  const [outroText, setOutroText] = useState("");

  useEffect(() => {
    if (open) {
      setSelected("valor");
      setOutroText("");
    }
  }, [open]);

  const handleConfirm = () => {
    let reason;
    if (selected === "outro") {
      const trimmed = (outroText || "").trim();
      if (!trimmed) {
        toast.warn('Descreva o motivo no campo "Outro"');
        return;
      }
      reason = trimmed.slice(0, 255);
    } else {
      const meta = LOST_REASONS.find((r) => r.key === selected);
      reason = meta ? meta.label : selected;
    }
    onConfirm(reason);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="xs"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
      TransitionComponent={Fade}
    >
      <Box className={classes.dialogHeader}>
        <Box className={classes.dialogHeaderIdentity}>
          <Box
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: `${theme.palette.error.main}18`,
              color: theme.palette.error.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <Cancel style={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography className={classes.dialogName}>Lead perdido</Typography>
            <Typography className={classes.dialogSubtitle}>
              {lead ? lead.name : ""} — qual o motivo?
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <Close />
        </IconButton>
      </Box>

      <DialogContent className={classes.dialogBody}>
        <RadioGroup
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
        >
          {LOST_REASONS.map((r) => {
            const RIcon = r.Icon;
            return (
              <FormControlLabel
                key={r.key}
                value={r.key}
                control={<Radio color="primary" size="small" />}
                label={
                  <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <RIcon style={{ fontSize: 18, color: theme.palette.text.secondary }} />
                    <span style={{ fontSize: "0.875rem" }}>{r.label}</span>
                  </Box>
                }
              />
            );
          })}
        </RadioGroup>

        {selected === "outro" && (
          <TextField
            multiline
            minRows={2}
            fullWidth
            variant="outlined"
            size="small"
            value={outroText}
            onChange={(e) => setOutroText(e.target.value.slice(0, 255))}
            placeholder="Descreva o motivo (até 255 caracteres)…"
            style={{ marginTop: theme.spacing(1.5) }}
            autoFocus
          />
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActionsRow}>
        <Button variant="outlined" onClick={onClose} size="small">
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disableElevation
          style={{ background: theme.palette.error.main, color: "#fff" }}
        >
          Marcar como perdido
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------- MANAGE TAGS DIALOG (Fase 4.3 v1.5) ----------
// CRUD admin das tags do tenant. Cores pre-definidas (palette curada) +
// pode inserir hex manual. Editar: clica no nome (inline) ou no swatch.
// Deletar: icone trash (com confirm). Criar: campo nome + cor + Adicionar.

const TAG_COLOR_PALETTE = [
  "#e53935", // red
  "#ec407a", // pink
  "#ab47bc", // purple
  "#5e35b1", // deep-purple
  "#3949ab", // indigo
  "#1e88e5", // blue
  "#039be5", // light-blue
  "#00897b", // teal
  "#43a047", // green
  "#7cb342", // light-green
  "#fdd835", // yellow
  "#fb8c00", // orange
  "#f4511e", // deep-orange
  "#8d6e63", // brown
  "#607d8b" // blue-grey
];

const ManageTagsDialog = ({
  open,
  onClose,
  tags,
  onCreate,
  onUpdate,
  onDelete,
  classes,
  theme
}) => {
  const isMobile = useIsMobile();
  const [name, setName] = useState("");
  const [color, setColor] = useState(TAG_COLOR_PALETTE[14]);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [editingColor, setEditingColor] = useState("");

  useEffect(() => {
    if (!open) {
      setName("");
      setColor(TAG_COLOR_PALETTE[14]);
      setEditingId(null);
    }
  }, [open]);

  const handleAdd = () => {
    if (!name.trim()) return;
    onCreate(name.trim(), color);
    setName("");
  };

  const startEdit = (tag) => {
    setEditingId(tag.id);
    setEditingName(tag.name);
    setEditingColor(tag.color || TAG_COLOR_PALETTE[14]);
  };

  const saveEdit = () => {
    if (!editingId || !editingName.trim()) return;
    onUpdate(editingId, { name: editingName.trim(), color: editingColor });
    setEditingId(null);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="xs"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
      TransitionComponent={Fade}
    >
      <Box className={classes.dialogHeader}>
        <Box className={classes.dialogHeaderIdentity}>
          <Box
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: `${theme.palette.primary.main}18`,
              color: theme.palette.primary.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <LocalOffer style={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography className={classes.dialogName}>Gerenciar Tags</Typography>
            <Typography className={classes.dialogSubtitle}>
              {tags.length} tag{tags.length === 1 ? "" : "s"} cadastrada
              {tags.length === 1 ? "" : "s"}
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <Close />
        </IconButton>
      </Box>

      <DialogContent className={classes.dialogBody}>
        {/* Criar nova */}
        <Typography className={classes.metaLabel}>Nova tag</Typography>
        <Box className={classes.tagFormRow}>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Nome (ex: VIP, Indicação)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAdd();
            }}
            style={{ flex: 1, minWidth: 160 }}
            inputProps={{ maxLength: 64 }}
          />
          <Button
            variant="contained"
            size="small"
            color="primary"
            startIcon={<Add style={{ fontSize: 14 }} />}
            onClick={handleAdd}
            disableElevation
            disabled={!name.trim()}
          >
            Adicionar
          </Button>
        </Box>
        <Box className={classes.tagColorPalette} style={{ marginTop: 8 }}>
          {TAG_COLOR_PALETTE.map((c) => (
            <span
              key={c}
              className={`${classes.tagColorDot} ${
                color === c ? classes.tagColorDotSelected : ""
              }`}
              style={{ backgroundColor: c }}
              onClick={() => setColor(c)}
              title={c}
            />
          ))}
        </Box>

        <Divider style={{ margin: `${theme.spacing(2)}px 0` }} />

        {/* Lista existente */}
        <Typography className={classes.metaLabel}>Tags existentes</Typography>
        {tags.length === 0 ? (
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ fontSize: "0.8125rem", marginTop: 8 }}
          >
            Nenhuma tag ainda. Crie a primeira acima.
          </Typography>
        ) : (
          <Box className={classes.tagDialogList}>
            {tags.map((tg) =>
              editingId === tg.id ? (
                <Box key={tg.id} className={classes.tagDialogRow}>
                  <span
                    className={classes.tagSwatch}
                    style={{ backgroundColor: editingColor }}
                  />
                  <TextField
                    size="small"
                    variant="outlined"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveEdit();
                      if (e.key === "Escape") setEditingId(null);
                    }}
                    style={{ flex: 1 }}
                    autoFocus
                    inputProps={{ maxLength: 64 }}
                  />
                  <Tooltip title="Salvar" arrow>
                    <IconButton size="small" onClick={saveEdit}>
                      <CheckCircle
                        style={{
                          fontSize: 18,
                          color: theme.palette.success.main
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Cancelar" arrow>
                    <IconButton size="small" onClick={() => setEditingId(null)}>
                      <Close style={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                <Box key={tg.id} className={classes.tagDialogRow}>
                  <span
                    className={classes.tagSwatch}
                    style={{ backgroundColor: tg.color || "#607d8b" }}
                  />
                  <Typography className={classes.tagDialogName}>
                    {tg.name}
                  </Typography>
                  <Tooltip title="Editar" arrow>
                    <IconButton size="small" onClick={() => startEdit(tg)}>
                      <EditOutlined style={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remover" arrow>
                    <IconButton size="small" onClick={() => onDelete(tg)}>
                      <DeleteOutline
                        style={{
                          fontSize: 16,
                          color: theme.palette.error.main
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              )
            )}
          </Box>
        )}

        {/* Color edit palette quando editando */}
        {editingId && (
          <Box style={{ marginTop: theme.spacing(1) }}>
            <Typography
              className={classes.metaLabel}
              style={{ marginBottom: 6 }}
            >
              Mudar cor
            </Typography>
            <Box className={classes.tagColorPalette}>
              {TAG_COLOR_PALETTE.map((c) => (
                <span
                  key={c}
                  className={`${classes.tagColorDot} ${
                    editingColor === c ? classes.tagColorDotSelected : ""
                  }`}
                  style={{ backgroundColor: c }}
                  onClick={() => setEditingColor(c)}
                  title={c}
                />
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions style={{ padding: theme.spacing(1.5, 3) }}>
        <Button onClick={onClose} size="small">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------- MODO FOCO DA LIGACAO (Fase 4.1 v1.5) ----------
// View fullscreen pra usar DURANTE a ligacao. Mostra script grande,
// objecoes ao lado, telefone clicavel, WhatsApp, resultado em 4 botoes
// claros (Agendou / Voltar a ligar / Nao atendeu / Perdido). ESC fecha.

const FocusModeDialog = ({
  open,
  lead,
  onClose,
  onResult,
  onWhatsApp,
  classes,
  theme
}) => {
  // ESC fecha (Material-UI Dialog ja faz, mas mantemos explicito)
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open || !lead) return null;

  const prio = PRIORITIES[lead.priority] || PRIORITIES.semana;
  const stage = STAGES.find((s) => s.key === lead.stage) || STAGES[0];
  const phoneClean = cleanPhone(lead.phone);
  const phoneDisplay = lead.phone || "—";
  const script = Array.isArray(lead.script) ? lead.script : [];
  const objections = Array.isArray(lead.objections) ? lead.objections : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      TransitionComponent={Fade}
      classes={{ paper: classes.focusPaper }}
    >
      {/* Header */}
      <Box className={classes.focusHeader}>
        <Avatar className={classes.focusAvatar}>{initials(lead.name)}</Avatar>
        <Box className={classes.focusIdentity}>
          <Typography className={classes.focusName}>
            {lead.name || lead.phone || "—"}
          </Typography>
          <Box className={classes.focusSub}>
            <span>{lead.procedure || "Sem procedimento"}</span>
            <span style={{ opacity: 0.5 }}>·</span>
            <span>{stage.label}</span>
            <span
              className={classes.focusPriorityChip}
              style={{
                backgroundColor: `${prio.tint}1f`,
                color: prio.tint,
                border: `1px solid ${prio.tint}40`
              }}
            >
              {prio.label}
            </span>
            {Array.isArray(lead.tags) &&
              lead.tags.slice(0, 4).map((tg) => (
                <span
                  key={tg.id}
                  className={classes.focusPriorityChip}
                  style={{
                    backgroundColor: `${tg.color || "#607d8b"}1f`,
                    color: tg.color || "#607d8b",
                    border: `1px solid ${tg.color || "#607d8b"}40`
                  }}
                >
                  {tg.name}
                </span>
              ))}
          </Box>
        </Box>
        <Tooltip title="Sair do Modo Foco (ESC)" arrow>
          <IconButton onClick={onClose} size="medium">
            <Close style={{ fontSize: 26 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Body — 3 colunas em desktop, 1 coluna mobile */}
      <Box className={classes.focusBody}>
        <Box className={classes.focusGrid}>
          {/* Coluna esquerda — contato + WhatsApp */}
          <Box className={classes.focusCard}>
            <Typography className={classes.focusCardTitle}>
              <Phone style={{ fontSize: 14, verticalAlign: "middle", marginRight: 4 }} />
              Contato
            </Typography>
            {phoneClean ? (
              <a
                href={`tel:+55${phoneClean}`}
                className={classes.focusPhoneBig}
              >
                {phoneDisplay}
              </a>
            ) : (
              <Typography
                style={{
                  fontSize: "1.25rem",
                  color: theme.palette.text.secondary,
                  fontStyle: "italic"
                }}
              >
                Sem telefone
              </Typography>
            )}

            <Box className={classes.focusInfoRow}>
              <span className={classes.focusInfoLabel}>Origem</span>
              {lead.origin || "—"}
            </Box>
            <Box className={classes.focusInfoRow}>
              <span className={classes.focusInfoLabel}>Profissão</span>
              {lead.profession || "—"}
            </Box>
            <Box className={classes.focusInfoRow}>
              <span className={classes.focusInfoLabel}>Consulta</span>
              {lead.consultDate || "—"}
            </Box>
            {lead.totalValue ? (
              <Box className={classes.focusInfoRow}>
                <span className={classes.focusInfoLabel}>Orçamento total</span>
                <strong>{brl(lead.totalValue)}</strong>
              </Box>
            ) : null}

            <Button
              variant="contained"
              startIcon={<WhatsApp />}
              onClick={onWhatsApp}
              className={classes.focusWaBtn}
              disableElevation
              disabled={!phoneClean}
            >
              Abrir WhatsApp
            </Button>
          </Box>

          {/* Coluna centro — Script */}
          <Box className={classes.focusCard}>
            <Typography className={classes.focusCardTitle}>
              <RecordVoiceOver
                style={{ fontSize: 14, verticalAlign: "middle", marginRight: 4 }}
              />
              Script da Ligação
            </Typography>
            {script.length === 0 ? (
              <Typography
                style={{
                  fontSize: "0.875rem",
                  color: theme.palette.text.secondary,
                  fontStyle: "italic"
                }}
              >
                Sem script. Edite o lead pra adicionar.
              </Typography>
            ) : (
              script.map((s, i) => (
                <Box key={i} className={classes.focusScriptStep}>
                  <Typography className={classes.focusScriptTitle}>
                    <span
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        background: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.75rem",
                        fontWeight: 700
                      }}
                    >
                      {i + 1}
                    </span>
                    {s.titulo || "Etapa"}
                  </Typography>
                  <Typography className={classes.focusScriptText}>
                    {s.fala || ""}
                  </Typography>
                  {s.nota && (
                    <Box className={classes.focusScriptNote}>
                      💡 {s.nota}
                    </Box>
                  )}
                </Box>
              ))
            )}
          </Box>

          {/* Coluna direita — objecoes + notas previas */}
          <Box className={classes.focusCard}>
            <Typography className={classes.focusCardTitle}>
              <MoreHoriz
                style={{ fontSize: 14, verticalAlign: "middle", marginRight: 4 }}
              />
              Objeções & Notas
            </Typography>
            {objections.length === 0 && !lead.notes && (
              <Typography
                style={{
                  fontSize: "0.875rem",
                  color: theme.palette.text.secondary,
                  fontStyle: "italic"
                }}
              >
                Sem objeções pré-cadastradas.
              </Typography>
            )}
            {objections.map((o, i) => (
              <Box key={i} className={classes.focusObjectionItem}>
                <Typography className={classes.focusObjectionLabel}>
                  Se disser: "{o.obj}"
                </Typography>
                <Typography className={classes.focusObjectionResp}>
                  "{o.resp}"
                </Typography>
              </Box>
            ))}
            {lead.notes && (
              <Box
                className={classes.focusObjectionItem}
                style={{ marginTop: theme.spacing(1) }}
              >
                <Typography className={classes.focusObjectionLabel}>
                  Anotações anteriores
                </Typography>
                <Typography
                  className={classes.focusObjectionResp}
                  style={{ fontStyle: "normal", whiteSpace: "pre-wrap" }}
                >
                  {lead.notes}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Footer — Resultado da ligacao */}
      <Box className={classes.focusResultBar}>
        <Button
          variant="outlined"
          className={classes.focusResultBtn}
          startIcon={<PhoneDisabled />}
          onClick={() => onResult("nao_atendeu")}
        >
          Não atendeu
        </Button>
        <Button
          variant="outlined"
          className={classes.focusResultBtn}
          startIcon={<Schedule />}
          onClick={() => onResult("ligar_amanha")}
        >
          Voltar a ligar
        </Button>
        <Button
          variant="outlined"
          className={classes.focusResultBtn}
          startIcon={<SentimentDissatisfied />}
          style={{ color: theme.palette.error.main, borderColor: theme.palette.error.main }}
          onClick={() => onResult("perdido")}
        >
          Perdido
        </Button>
        <Button
          variant="contained"
          className={classes.focusResultBtn}
          startIcon={<EventAvailable />}
          color="primary"
          disableElevation
          onClick={() => onResult("agendou")}
        >
          Agendou ✓
        </Button>
      </Box>
    </Dialog>
  );
};

// ---------- CALENDAR / AGENDA VIEW (Fase 4.4 v1.5) ----------
// Lista cronologica de leads stage='agendado' agrupados por mes. Click
// no item abre o detail modal. consultDate parseado de varios formatos
// (DD/MM/YYYY, DD/MM, YYYY-MM-DD, ISO). Sem grid mensal completo —
// lista vertical e mais util pro volume real (~10-30 cirurgias/mes).

const MONTH_LABEL_FULL = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro"
];
const MONTH_LABEL_SHORT = [
  "JAN",
  "FEV",
  "MAR",
  "ABR",
  "MAI",
  "JUN",
  "JUL",
  "AGO",
  "SET",
  "OUT",
  "NOV",
  "DEZ"
];

// Parser flexivel pra consultDate string (varios formatos comuns BR + ISO).
// Retorna Date OR null se nao conseguir parsear.
const tryParseConsultDate = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  // ISO-like: YYYY-MM-DD ou YYYY-MM-DDTHH:MM:SS
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const d = new Date(s);
    if (!isNaN(d.getTime())) return d;
  }
  // BR full: DD/MM/YYYY
  const mFull = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (mFull) {
    const d = new Date(
      Number(mFull[3]),
      Number(mFull[2]) - 1,
      Number(mFull[1])
    );
    if (!isNaN(d.getTime())) return d;
  }
  // BR short: DD/MM (assume ano atual; se data passou, +1 ano)
  const mShort = s.match(/^(\d{1,2})\/(\d{1,2})$/);
  if (mShort) {
    const year = new Date().getFullYear();
    let d = new Date(year, Number(mShort[2]) - 1, Number(mShort[1]));
    if (d.getTime() < Date.now() - 24 * 3600 * 1000) {
      d = new Date(year + 1, Number(mShort[2]) - 1, Number(mShort[1]));
    }
    if (!isNaN(d.getTime())) return d;
  }
  // Fallback: tentar Date direto (cobre "Mar 15 2026" etc.)
  const d = new Date(s);
  if (!isNaN(d.getTime())) return d;
  return null;
};

const CalendarView = ({ leads, classes, theme, onOpenLead }) => {
  // Filtrar so os agendados com consultDate parseavel
  const items = (Array.isArray(leads) ? leads : [])
    .filter((l) => l.stage === "agendado")
    .map((l) => ({
      lead: l,
      date: tryParseConsultDate(l.consultDate)
    }))
    .filter((x) => x.date !== null);

  // Sort por data (proximas primeiro)
  items.sort((a, b) => a.date.getTime() - b.date.getTime());

  if (items.length === 0) {
    return (
      <Box className={classes.calendarRoot}>
        <Box className={classes.calendarEmpty}>
          <EventAvailable
            style={{
              fontSize: 48,
              color: theme.palette.text.disabled,
              marginBottom: 8
            }}
          />
          <Typography style={{ fontSize: "0.9375rem", fontWeight: 600 }}>
            Nenhuma cirurgia agendada com data definida.
          </Typography>
          <Typography style={{ fontSize: "0.8125rem", marginTop: 4 }}>
            Mova leads pra "Agendado" e preencha o campo "Consulta" pra aparecer aqui.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Agrupar por (ano-mes)
  const groups = [];
  let cur = null;
  items.forEach((it) => {
    const key = `${it.date.getFullYear()}-${it.date.getMonth()}`;
    if (!cur || cur.key !== key) {
      cur = {
        key,
        year: it.date.getFullYear(),
        month: it.date.getMonth(),
        items: []
      };
      groups.push(cur);
    }
    cur.items.push(it);
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <Box className={classes.calendarRoot}>
      {groups.map((g) => (
        <Box key={g.key} className={classes.calendarMonthGroup}>
          <Typography className={classes.calendarMonthTitle}>
            {MONTH_LABEL_FULL[g.month]} {g.year} · {g.items.length}{" "}
            {g.items.length === 1 ? "cirurgia" : "cirurgias"}
          </Typography>
          {g.items.map(({ lead, date }) => {
            const day = date.getDate();
            const monthShort = MONTH_LABEL_SHORT[date.getMonth()];
            const isPast = date.getTime() < today.getTime();
            const isToday = date.getTime() === today.getTime();
            const checklist = Array.isArray(lead.preopChecklist)
              ? lead.preopChecklist
              : null;
            const done = checklist
              ? checklist.filter((i) => i.checked).length
              : null;
            const total = checklist ? checklist.length : null;
            const tags = Array.isArray(lead.tags) ? lead.tags : [];
            return (
              <Box
                key={lead.id}
                className={classes.calendarRow}
                onClick={() => onOpenLead(lead)}
              >
                <Box
                  className={classes.calendarDateBadge}
                  style={
                    isPast
                      ? {
                          background: theme.palette.error.main,
                          color: "#fff"
                        }
                      : isToday
                      ? {
                          background: theme.palette.warning.main,
                          color: "#fff"
                        }
                      : undefined
                  }
                >
                  <Typography className={classes.calendarDateDay}>
                    {day}
                  </Typography>
                  <Typography className={classes.calendarDateMonth}>
                    {monthShort}
                  </Typography>
                </Box>
                <Box className={classes.calendarRowMain}>
                  <Typography className={classes.calendarRowName}>
                    {lead.name || lead.phone || "—"}
                  </Typography>
                  <Box className={classes.calendarRowMeta}>
                    <span>{lead.procedure || "Procedimento não informado"}</span>
                    {total !== null && total > 0 && (
                      <span className={classes.calendarChecklistChip}>
                        <AssignmentTurnedIn style={{ fontSize: 12 }} />
                        {done}/{total} checklist
                      </span>
                    )}
                    {tags.slice(0, 3).map((tg) => (
                      <span
                        key={tg.id}
                        className={classes.cardTagChip}
                        style={{
                          backgroundColor: tg.color || "#607d8b"
                        }}
                      >
                        {tg.name}
                      </span>
                    ))}
                  </Box>
                </Box>
                <Box>
                  {isToday && (
                    <span
                      className={classes.calendarChecklistChip}
                      style={{
                        background: theme.palette.warning.main,
                        color: "#fff"
                      }}
                    >
                      HOJE
                    </span>
                  )}
                  {isPast && !isToday && (
                    <span
                      className={classes.calendarChecklistChip}
                      style={{
                        background: theme.palette.error.main,
                        color: "#fff"
                      }}
                    >
                      ATRASADO
                    </span>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

// ---------- ACTIVITY TIMELINE (Fase 3 v1.5) ----------
// Busca + renderiza cronologia reversa de atividades do lead.
// Cada kind tem icone + cor; usuario que fez aparece no rodape; tempo
// relativo + absoluto. Polling NAO — re-fetch ao trocar de tab.

const ACTIVITY_META = {
  create:            { Icon: Add,                 tint: "#06c270", label: "Lead criado" },
  stage:             { Icon: ArrowForwardIos,     tint: "#03a9f4", label: "Stage" },
  priority:          { Icon: PriorityHighRounded, tint: "#ff9800", label: "Prioridade" },
  notes:             { Icon: Notes,               tint: "#607d8b", label: "Anotação" },
  next_contact:      { Icon: Schedule,            tint: "#4254cb", label: "Próximo contato" },
  lost_reason:       { Icon: Cancel,              tint: "#e53935", label: "Motivo da perda" },
  checklist_init:    { Icon: AssignmentTurnedIn,  tint: "#8e24aa", label: "Checklist criado" },
  checklist_done:    { Icon: CheckBoxIcon,        tint: "#06c270", label: "Item concluído" },
  checklist_undone:  { Icon: CheckBoxOutlineBlank,tint: "#9e9e9e", label: "Item desmarcado" },
  tags:              { Icon: LocalOffer,          tint: "#ec407a", label: "Tags" },
  remove:            { Icon: DeleteOutline,       tint: "#e53935", label: "Removido" },
  misc:              { Icon: MoreHoriz,           tint: "#607d8b", label: "Atualização" }
};

const relativeTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return "agora";
  if (diffSec < 3600) return `há ${Math.floor(diffSec / 60)}min`;
  if (diffSec < 86400) return `há ${Math.floor(diffSec / 3600)}h`;
  if (diffSec < 604800) return `há ${Math.floor(diffSec / 86400)}d`;
  return d.toLocaleDateString("pt-BR");
};

const ActivityTimeline = ({ leadId, classes, theme }) => {
  const [items, setItems] = useState(null);
  const [loadingActs, setLoadingActs] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoadingActs(true);
    listRescueLeadActivities(leadId)
      .then((data) => {
        if (!cancelled) setItems(Array.isArray(data) ? data : []);
      })
      .catch((e) => {
        console.error("[ActivityTimeline] load error", e);
        if (!cancelled) {
          setItems([]);
          toast.error("Erro ao carregar histórico");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingActs(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  if (loadingActs) {
    return (
      <Box style={{ display: "flex", justifyContent: "center", padding: 32 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (!items || items.length === 0) {
    return (
      <Box className={classes.timelineEmpty}>
        Sem atividade registrada ainda. As ações no lead (mudança de
        stage, prioridade, anotação…) vão aparecer aqui.
      </Box>
    );
  }
  return (
    <Box className={classes.timelineWrap}>
      {items.map((a) => {
        const meta = ACTIVITY_META[a.kind] || ACTIVITY_META.misc;
        const MIcon = meta.Icon;
        const userName =
          a.user && a.user.name ? a.user.name : "Sistema";
        return (
          <Box key={a.id} className={classes.timelineItem}>
            <Box
              className={classes.timelineDot}
              style={{
                background: `${meta.tint}20`,
                color: meta.tint
              }}
            >
              <MIcon style={{ fontSize: 16 }} />
            </Box>
            <Box className={classes.timelineContent}>
              <Typography className={classes.timelineDesc}>
                {a.description}
              </Typography>
              <Box className={classes.timelineMeta}>
                <span>{userName}</span>
                <span>·</span>
                <Tooltip
                  title={new Date(a.createdAt).toLocaleString("pt-BR")}
                  arrow
                >
                  <span>{relativeTime(a.createdAt)}</span>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default ResgateComercial;
